import React, { useState, useEffect } from 'react';
import { QRCodeSVG } from 'qrcode.react';
import { motion, AnimatePresence } from 'motion/react';
import { 
  Search, 
  Loader2, 
  Edit2, 
  Package,
  X,
  XCircle,
  Tag,
  Check,
  AlertCircle,
  Eye,
  History,
  Store,
  User as UserIcon,
  Clock,
  Hash,
  ArrowUpRight,
  ArrowDownLeft,
  Layers,
  Calendar,
  Info,
  Settings,
  MoreVertical,
  ArrowRightLeft,
  ExternalLink,
  Trash2,
  PlusCircle,
  MinusCircle,
  Image as ImageIcon,
  EyeOff,
  ClipboardList,
  GitBranch,
  Printer,
  Barcode as BarcodeIcon,
  Layout,
  QrCode
} from 'lucide-react';
import JsBarcode from 'jsbarcode';
import { notify } from '../services/notify';
import { 
  db, 
  auth, 
  collection, 
  query, 
  where, 
  onSnapshot, 
  updateDoc,
  addDoc,
  doc,
  getDoc,
  serverTimestamp,
  orderBy,
  limit,
  storage,
  ref,
  uploadBytes,
  uploadBytesResumable,
  getDownloadURL
} from '../firebase';
import { cn } from '../lib/utils';
import { localizePrintableHtml } from '../lib/localization';
import {
  type UOM,
  compatibleUoms,
  getProductUomId,
  resolveUom,
  withProductUom,
} from '../lib/uom';

const getLocalAuthToken = () => typeof window !== 'undefined' ? localStorage.getItem('app_auth_token') : null;
const isLocalSession = () => Boolean(getLocalAuthToken());
const getLocalApiHeaders = () => ({ 'Content-Type': 'application/json', 'Authorization': `Bearer ${getLocalAuthToken() || ''}` });

enum OperationType {
  CREATE = 'create',
  UPDATE = 'update',
  DELETE = 'delete',
  LIST = 'list',
  GET = 'get',
  WRITE = 'write',
}

interface FirestoreErrorInfo {
  error: string;
  operationType: OperationType;
  path: string | null;
  authInfo: {
    userId?: string | null;
    email?: string | null;
    emailVerified?: boolean | null;
    isAnonymous?: boolean | null;
    tenantId?: string | null;
    providerInfo?: {
      providerId?: string | null;
      email?: string | null;
    }[];
  }
}

function handleFirestoreError(error: unknown, operationType: OperationType, path: string | null) {
  const errInfo: FirestoreErrorInfo = {
    error: error instanceof Error ? error.message : String(error),
    authInfo: {
      userId: auth.currentUser?.uid,
      email: auth.currentUser?.email,
      emailVerified: auth.currentUser?.emailVerified,
      isAnonymous: auth.currentUser?.isAnonymous,
      tenantId: auth.currentUser?.tenantId,
      providerInfo: auth.currentUser?.providerData?.map(provider => ({
        providerId: provider.providerId,
        email: provider.email,
      })) || []
    },
    operationType,
    path
  };
  console.error('Firestore Error: ', JSON.stringify(errInfo));
  const isPermissionDenied = error instanceof Error && error.message.includes('Missing or insufficient permissions');
  const isLocalhost = typeof window !== 'undefined' && ['localhost', '127.0.0.1', '::1'].includes(window.location.hostname);
  if (isLocalhost && isPermissionDenied) {
    console.warn(`Ignoring Firestore permission error at ${path} during local development.`);
    return;
  }
  throw new Error(JSON.stringify(errInfo));
}

interface RecipeItem {
  itemId: string;
  name: string;
  quantity: number;
  unit: string;
}

interface Recipe {
  id: string;
  name: string;
  isActive: boolean;
  components: RecipeItem[];
}

interface VariantOption {
  name: string;
  values: string[];
}

interface Variant {
  id: string;
  name: string;
  sku: string;
  options: { [key: string]: string };
  costPrice: number;
  unitPrice: number;
  barcode: string;
  quantity: number;
  save?: boolean;
}

interface Item {
  id: string;
  orgId: string;
  code: string;
  name: string;
  category?: string;
  description: string;
  unitPrice: number;
  costPrice?: number;
  quantity?: number;
  type: 'product' | 'service';
  status: 'active' | 'archived';
  enablePOS?: boolean;
  trackInventory?: boolean;
  stockUomId?: string;
  stockUomName?: string;
  stockUomCategory?: string;
  purchaseUomId?: string;
  purchaseUomName?: string;
  purchaseUomCategory?: string;
  salesUomId?: string;
  salesUomName?: string;
  salesUomCategory?: string;
  barcode?: string;
  imageUrl?: string;
  ingredients?: string[];
  createdAt: any;
  createdBy?: string;
  allowNegativeStock?: boolean;
  hasExpiry?: boolean;
  expiryDate?: string;
  hasVariants?: boolean;
  variantOptions?: VariantOption[];
  variants?: Variant[];
  recipeManagement?: boolean;
  recipeName?: string;
  recipe?: RecipeItem[];
  recipes?: Recipe[];
  hideFromSellScreen?: boolean;
  tags?: string[];
  storeStock?: { [storeId: string]: number };
  parentId?: string;
  isVariant?: boolean;
}

interface StockMovement {
  id: string;
  itemId: string;
  storeId: string;
  type: 'sale' | 'purchase' | 'adjustment' | 'return' | 'transfer_in' | 'transfer_out' | 'write_off' | 'single stock take';
  quantity: number;
  previousStock: number;
  newStock: number;
  referenceId?: string; // e.g., invoice ID or PO ID
  referenceNumber?: string;
  targetStoreId?: string; // for transfers
  note?: string;
  createdBy: string;
  createdByName: string;
  createdAt: any;
}

interface StockActionState {
  type: 'transfer' | 'write_off' | 'stock_take' | null;
  storeId: string | null;
}

interface POSCatalogViewProps {
  orgId: string;
  language: 'en' | 'ar';
}

const generateVariants = (options: VariantOption[], basePrice: number, baseCost: number, baseCode: string) => {
  if (options.length === 0) return [];
  
  // Filter out options with no values
  const validOptions = options.filter(o => o.values.length > 0);
  if (validOptions.length === 0) return [];

  const combinations = (acc: any[], current: VariantOption): any[] => {
    if (acc.length === 0) return current.values.map(v => ({ [current.name]: v }));
    const result: any[] = [];
    acc.forEach(a => {
      current.values.forEach(v => {
        result.push({ ...a, [current.name]: v });
      });
    });
    return result;
  };

  const allCombinations = validOptions.reduce(combinations, []);

  return allCombinations.map((combo, idx) => {
    const name = Object.values(combo).join(' / ');
    return {
      id: Math.random().toString(36).substr(2, 9),
      name,
      sku: `${baseCode}-${idx + 1}`,
      options: combo,
      costPrice: baseCost,
      unitPrice: basePrice,
      barcode: '',
      quantity: 0,
      save: true
    };
  });
};

export default function POSCatalogView({ orgId, language = 'en' }: POSCatalogViewProps) {
  const [items, setItems] = useState<Item[]>([]);
  const [uoms, setUoms] = useState<UOM[]>([]);
  const [movementItemIds, setMovementItemIds] = useState<Set<string>>(new Set());
  const [stores, setStores] = useState<any[]>([]);
  const [users, setUsers] = useState<any[]>([]);
  const [loading, setLoading] = useState(true);
  const [searchQuery, setSearchQuery] = useState('');
  const [editingItem, setEditingItem] = useState<Item | null>(null);
  const [viewingItem, setViewingItem] = useState<Item | null>(null);
  const [isEditModalOpen, setIsEditModalOpen] = useState(false);
  const [isViewModalOpen, setIsViewModalOpen] = useState(false);
  const [stockLog, setStockLog] = useState<StockMovement[]>([]);
  const [loadingLog, setLoadingLog] = useState(false);
  const [activeStoreLogId, setActiveStoreLogId] = useState<string | 'all'>('all');
  const [openMenuStoreId, setOpenMenuStoreId] = useState<string | null>(null);
  const [categories, setCategories] = useState<string[]>([]);
  const [isUploading, setIsUploading] = useState(false);
  const [uploadProgress, setUploadProgress] = useState<number | null>(null);
  
  // QR Code States
  const [isQrModalOpen, setIsQrModalOpen] = useState(false);
  const [qrLoading, setQrLoading] = useState(false);
  const [qrDataUrl, setQrDataUrl] = useState<string | null>(null);
  const [currentQrItem, setCurrentQrItem] = useState<Item | null>(null);
  const [isCatalogQrModalOpen, setIsCatalogQrModalOpen] = useState(false);

  // Barcode Label Settings
  const [barcodeSettings, setBarcodeSettings] = useState({
    showName: true,
    showPrice: true,
    showCode: true,
    showOrgName: true,
    showQr: true,
    labelWidth: 40, // mm
    labelHeight: 25, // mm
    fontSize: 12,
    barcodeHeight: 40
  });
  const barcodeRef = React.useRef<SVGSVGElement>(null);

  // Stock Action States
  const [stockAction, setStockAction] = useState<StockActionState>({ type: null, storeId: null });
  const [actionQuantity, setActionQuantity] = useState<number>(0);
  const [actionNote, setActionNote] = useState<string>('');
  const [targetStoreId, setTargetStoreId] = useState<string>('');
  const [isProcessingAction, setIsProcessingAction] = useState(false);

  const [catalogSettings, setCatalogSettings] = useState<any>(null);

  useEffect(() => {
    if (!orgId) return;
    if (isLocalSession()) {
      let cancelled = false;
      Promise.all([
        fetch(`/api/organizations/${orgId}/uoms`, { headers: getLocalApiHeaders() }).then(r => r.ok ? r.json() : []),
        fetch(`/api/organizations/${orgId}/inventory-movements`, { headers: getLocalApiHeaders() }).then(r => r.ok ? r.json() : []),
      ]).then(([units, inventoryMovements]) => {
        if (cancelled) return;
        setUoms(Array.isArray(units) ? units : []);
        setMovementItemIds(new Set((Array.isArray(inventoryMovements) ? inventoryMovements : []).map((movement: any) =>
          movement?.itemId || movement?.item_id || movement?.productId || movement?.product_id
        ).filter((id: unknown): id is string => typeof id === 'string')));
      }).catch(error => { if (!cancelled) console.error('UOM or movement metadata load failed:', error); });
      return () => { cancelled = true; };
    }
    const unitsQuery = query(collection(db, 'uoms'), where('orgId', '==', orgId));
    const movementsQuery = query(collection(db, 'stock_movements'), where('orgId', '==', orgId), limit(500));
    const unsubscribeUnits = onSnapshot(unitsQuery, snapshot => setUoms(snapshot.docs.map(unit => ({ id: unit.id, ...unit.data() } as UOM))), error => console.error('UOM metadata load failed:', error));
    const unsubscribeMovements = onSnapshot(movementsQuery, snapshot => setMovementItemIds(new Set(snapshot.docs.map(movement => movement.data().itemId || movement.data().productId).filter((id): id is string => typeof id === 'string'))), error => console.error('Movement metadata load failed:', error));
    return () => { unsubscribeUnits(); unsubscribeMovements(); };
  }, [orgId]);

  useEffect(() => {
    if (!orgId) return;

    if (isLocalSession()) {
      let cancelled = false;
      Promise.all([
        fetch(`/api/organizations/${orgId}/settings`, { headers: getLocalApiHeaders() }).then(r => r.ok ? r.json() : null),
        fetch(`/api/organizations/${orgId}/items`, { headers: getLocalApiHeaders() }).then(r => r.json()),
        fetch(`/api/organizations/${orgId}/stores`, { headers: getLocalApiHeaders() }).then(r => r.json()),
        fetch(`/api/organizations/${orgId}/users`, { headers: getLocalApiHeaders() }).then(r => r.json()),
        fetch(`/api/organizations/${orgId}/product-categories`, { headers: getLocalApiHeaders() }).then(r => r.json()),
      ]).then(([settings, itemsData, storesData, usersData, cats]) => {
        if (cancelled) return;
        setCatalogSettings(settings?.catalogSettings || settings?.data?.catalogSettings || {});
        const posItems = Array.isArray(itemsData) ? itemsData.filter((i: any) => i.enablePOS && i.status === 'active') as Item[] : [];
        setItems(posItems);
        setStores(Array.isArray(storesData) ? storesData.filter((s: any) => s.status === 'active') : []);
        setUsers(Array.isArray(usersData) ? usersData : []);
        setCategories(Array.isArray(cats) ? cats.map((c: any) => c.name) : []);
        setLoading(false);
      }).catch(err => {
        if (cancelled) return;
        console.error('POS catalog local load failed:', err);
        setLoading(false);
      });
      return () => { cancelled = true; };
    }

    // Fetch Catalog Settings from Organization doc
    const orgRef = doc(db, 'organizations', orgId);
    const unsubscribeOrg = onSnapshot(orgRef, (docSnap) => {
      if (docSnap.exists()) {
        const orgData = docSnap.data();
        setCatalogSettings(orgData.catalogSettings || {});
      }
    }, (error) => {
      handleFirestoreError(error, OperationType.GET, `organizations/${orgId}`);
    });

    // Fetch Items
    const q = query(
      collection(db, 'items'),
      where('orgId', '==', orgId),
      where('enablePOS', '==', true),
      where('status', '==', 'active')
    );

    const unsubscribeItems = onSnapshot(q, (snapshot) => {
      const itemsData = snapshot.docs.map(doc => ({ id: doc.id, ...doc.data() } as Item));
      setItems(itemsData);
      setLoading(false);
    }, (error) => {
      handleFirestoreError(error, OperationType.LIST, 'items');
      setLoading(false);
    });

    // Fetch Stores
    const storesQ = query(
      collection(db, 'stores'), 
      where('orgId', '==', orgId),
      where('status', '==', 'active')
    );
    const unsubscribeStores = onSnapshot(storesQ, (snapshot) => {
      setStores(snapshot.docs.map(doc => ({ id: doc.id, ...doc.data() })));
    }, (error) => {
      handleFirestoreError(error, OperationType.LIST, 'stores');
    });

    // Fetch Users (using org_users since root users collection is restricted)
    const usersQ = query(collection(db, 'org_users'), where('orgId', '==', orgId));
    const unsubscribeUsers = onSnapshot(usersQ, (snapshot) => {
      setUsers(snapshot.docs.map(doc => ({ id: doc.id, ...doc.data() })));
    }, (error) => {
      handleFirestoreError(error, OperationType.LIST, 'org_users');
    });

    // Fetch Categories
    const catsQ = query(collection(db, 'item_categories'), where('orgId', '==', orgId));
    const unsubscribeCats = onSnapshot(catsQ, (snapshot) => {
      setCategories(snapshot.docs.map(doc => doc.data().name));
    }, (error) => {
      handleFirestoreError(error, OperationType.LIST, 'item_categories');
    });

    return () => {
      unsubscribeItems();
      unsubscribeStores();
      unsubscribeUsers();
      unsubscribeCats();
    };
  }, [orgId]);

  const handleImageUpload = async (e: React.ChangeEvent<HTMLInputElement>) => {
    const file = e.target.files?.[0];
    if (!file || !editingItem) return;

    // Check file size (3MB limit)
    if (file.size > 3 * 1024 * 1024) {
      notify.error('Image size must be less than 3MB');
      return;
    }

    setIsUploading(true);
    setUploadProgress(0);
    try {
      if (isLocalSession()) {
        const token = getLocalAuthToken();
        const formData = new FormData();
        formData.append('file', file);
        formData.append('targetPath', `items/${Date.now()}_${file.name}`);
        const res = await fetch('/api/files/upload', {
          method: 'POST',
          headers: { Authorization: `Bearer ${token || ''}` },
          body: formData,
        });
        if (!res.ok) throw new Error('Upload failed');
        const result = await res.json();
        setEditingItem({ ...editingItem, imageUrl: result.url });
        notify.confirm('Image uploaded successfully');
        return;
      }
      const storageRef = ref(storage, `items/${orgId}/${editingItem.id}/${file.name}`);
      const uploadTask = uploadBytesResumable(storageRef, file);
      
      await new Promise((resolve, reject) => {
        uploadTask.on('state_changed',
          (snapshot) => {
            const progress = Math.round((snapshot.bytesTransferred / snapshot.totalBytes) * 100);
            setUploadProgress(progress);
          },
          (error) => reject(error),
          () => resolve(null)
        );
      });
      
      const downloadURL = await getDownloadURL(uploadTask.snapshot.ref);
      
      setEditingItem({ ...editingItem, imageUrl: downloadURL });
      notify.confirm('Image uploaded successfully');
    } catch (error: any) {
      console.error('Error uploading image:', error);
      notify.error(`Failed to upload image: ${error.message || 'Unknown error'}`);
    } finally {
      setIsUploading(false);
      setUploadProgress(null);
      if (e.target) e.target.value = '';
    }
  };

  useEffect(() => {
    if (viewingItem) {
      setLoadingLog(true);
      if (isLocalSession()) {
        let cancelled = false;
        fetch(`/api/organizations/${orgId}/inventory-movements?itemId=${viewingItem.id}`, { headers: getLocalApiHeaders() })
          .then(r => r.json())
          .then(data => {
            if (cancelled) return;
            // Filter client-side since the endpoint returns all org movements
            const filtered = Array.isArray(data) ? data.filter((m: any) => m.item_id === viewingItem.id || m.productId === viewingItem.id) : [];
            setStockLog(filtered as StockMovement[]);
            setLoadingLog(false);
          })
          .catch(err => {
            if (cancelled) return;
            console.error('Stock log load failed:', err);
            setLoadingLog(false);
          });
        return () => { cancelled = true; };
      }
      const logQ = query(
        collection(db, 'stock_movements'),
        where('itemId', '==', viewingItem.id),
        orderBy('createdAt', 'desc'),
        limit(50)
      );

      const unsubscribeLog = onSnapshot(logQ, (snapshot) => {
        setStockLog(snapshot.docs.map(doc => ({ id: doc.id, ...doc.data() } as StockMovement)));
        setLoadingLog(false);
      }, (error) => {
        handleFirestoreError(error, OperationType.LIST, 'stock_movements');
        setLoadingLog(false);
      });

      return () => unsubscribeLog();
    }
  }, [viewingItem]);

  const filteredItems = items.filter(item => 
    item.name.toLowerCase().includes(searchQuery.toLowerCase()) ||
    item.code.toLowerCase().includes(searchQuery.toLowerCase()) ||
    (item.barcode && item.barcode.includes(searchQuery))
  );

  const handleUpdateItem = async (e: React.FormEvent) => {
    e.preventDefault();
    if (!editingItem) return;
    if (!isLocalSession() && !auth.currentUser) return;

    try {
      if (isLocalSession()) {
        const { id, createdAt, ...payload } = editingItem as any;
        const res = await fetch(`/api/organizations/${orgId}/items/${editingItem.id}`, {
          method: 'PATCH', headers: getLocalApiHeaders(), body: JSON.stringify(payload),
        });
        if (!res.ok) { const e = await res.json().catch(() => null); throw new Error(e?.error || 'Could not update item'); }
        const updated = await res.json() as Item;
        setItems(prev => prev.map(i => i.id === updated.id ? updated : i));
        if (viewingItem?.id === editingItem.id) setViewingItem({ ...viewingItem, ...updated });
        notify.confirm('Product updated successfully');
        setIsEditModalOpen(false);
        setEditingItem(null);
        return;
      }

      const itemRef = doc(db, 'items', editingItem.id);
      const updateData = {
        name: editingItem.name,
        unitPrice: Number(editingItem.unitPrice),
        costPrice: Number(editingItem.costPrice || 0),
        barcode: editingItem.barcode || '',
        description: editingItem.description || '',
        ingredients: editingItem.ingredients || [],
        category: editingItem.category || '',
        imageUrl: editingItem.imageUrl || '',
        tags: editingItem.tags || [],
        recipeManagement: !!editingItem.recipeManagement,
        recipes: editingItem.recipes || [],
        hasVariants: !!editingItem.hasVariants,
        variantOptions: editingItem.variantOptions || [],
        variants: editingItem.variants || [],
        allowNegativeStock: !!editingItem.allowNegativeStock,
        hasExpiry: !!editingItem.hasExpiry,
        expiryDate: editingItem.expiryDate || '',
        hideFromSellScreen: !!editingItem.hideFromSellScreen,
        trackInventory: !!editingItem.trackInventory,
        stockUomId: editingItem.stockUomId || '',
        stockUomName: editingItem.stockUomName || '',
        stockUomCategory: editingItem.stockUomCategory || '',
        purchaseUomId: editingItem.purchaseUomId || '',
        purchaseUomName: editingItem.purchaseUomName || '',
        purchaseUomCategory: editingItem.purchaseUomCategory || '',
        salesUomId: editingItem.salesUomId || '',
        salesUomName: editingItem.salesUomName || '',
        salesUomCategory: editingItem.salesUomCategory || '',
        updatedAt: serverTimestamp()
      };

      await updateDoc(itemRef, updateData);

      // Save variants as normal products
      if (editingItem.hasVariants && editingItem.variants && editingItem.variants.length > 0) {
        const itemsPath = 'items';
        const variantsToSave = editingItem.variants.filter(v => v.save !== false);
        for (const variant of variantsToSave) {
          // Check if variant already exists (by SKU and parentId)
          const existingVariant = items.find(i => i.code === variant.sku && i.parentId === editingItem.id);
          
          if (!existingVariant) {
            await addDoc(collection(db, itemsPath), {
              code: variant.sku,
              name: `${editingItem.name} - ${variant.name}`,
              description: editingItem.description || '',
              unitPrice: variant.unitPrice,
              costPrice: variant.costPrice,
              quantity: variant.quantity,
              category: editingItem.category,
              type: editingItem.type,
              status: 'active',
              parentId: editingItem.id,
              isVariant: true,
              barcode: variant.barcode,
              enablePOS: editingItem.enablePOS,
              allowNegativeStock: editingItem.allowNegativeStock,
              hasExpiry: editingItem.hasExpiry,
              hideFromSellScreen: editingItem.hideFromSellScreen,
              imageUrl: editingItem.imageUrl,
              tags: editingItem.tags,
              orgId,
              createdBy: auth.currentUser.uid,
              createdAt: serverTimestamp()
            });
          } else {
            // Update existing variant
            const variantRef = doc(db, itemsPath, existingVariant.id);
            await updateDoc(variantRef, {
              name: `${editingItem.name} - ${variant.name}`,
              unitPrice: variant.unitPrice,
              costPrice: variant.costPrice,
              barcode: variant.barcode,
              category: editingItem.category,
              status: editingItem.status,
            });
          }
        }
      }

      notify.confirm('Product updated successfully');
      setIsEditModalOpen(false);
      
      // Update viewing item if it's the same one
      if (viewingItem?.id === editingItem.id) {
        setViewingItem({ ...viewingItem, ...editingItem });
      }
      
      setEditingItem(null);
    } catch (error) {
      handleFirestoreError(error, OperationType.UPDATE, 'items');
      notify.error('Failed to update product');
    }
  };

  const handleStockAction = async (e: React.FormEvent) => {
    e.preventDefault();
    if (!viewingItem || !stockAction.type || !stockAction.storeId || isProcessingAction) return;
    if (actionQuantity <= 0) {
      notify.error('Quantity must be greater than 0');
      return;
    }

    setIsProcessingAction(true);
    try {
      const sourceStoreId = stockAction.storeId;

      if (isLocalSession()) {
        let endpoint: string;
        let body: any;
        if (stockAction.type === 'transfer') {
          if (!targetStoreId || targetStoreId === sourceStoreId) {
            notify.error(targetStoreId ? 'Source and target stores must be different' : 'Please select a target store');
            setIsProcessingAction(false);
            return;
          }
          endpoint = `/api/organizations/${orgId}/inventory-transfers`;
          body = { productId: viewingItem.id, sourceLocationId: sourceStoreId, destinationLocationId: targetStoreId, quantity: actionQuantity, notes: actionNote };
        } else if (stockAction.type === 'write_off') {
          endpoint = `/api/organizations/${orgId}/inventory-adjustments`;
          body = { productId: viewingItem.id, locationId: sourceStoreId, quantity: -actionQuantity, reason: actionNote || 'Write-off' };
        } else {
          // stock_take – set absolute value via adjustment
          const currentQ = viewingItem.storeStock?.[sourceStoreId] || 0;
          const delta = actionQuantity - currentQ;
          endpoint = `/api/organizations/${orgId}/inventory-adjustments`;
          body = { productId: viewingItem.id, locationId: sourceStoreId, quantity: delta, reason: actionNote || 'Stock take' };
        }
        const res = await fetch(endpoint, { method: 'POST', headers: getLocalApiHeaders(), body: JSON.stringify(body) });
        if (!res.ok) { const e = await res.json().catch(() => null); throw new Error(e?.error || 'Could not process stock action'); }
        notify.confirm(stockAction.type === 'transfer' ? 'Stock transferred successfully' : stockAction.type === 'write_off' ? 'Stock written off successfully' : 'Stock adjusted successfully');
        setStockAction({ type: null, storeId: null });
        setActionQuantity(0);
        setActionNote('');
        setTargetStoreId('');
        setIsProcessingAction(false);
        return;
      }

      const currentSourceStock = viewingItem.storeStock?.[sourceStoreId] || 0;
      const itemRef = doc(db, 'items', viewingItem.id);
      const movementsCol = collection(db, 'stock_movements');
      const currentUser = auth.currentUser;

      if (stockAction.type === 'transfer') {
        if (!targetStoreId) {
          notify.error('Please select a target store');
          setIsProcessingAction(false);
          return;
        }
        if (targetStoreId === sourceStoreId) {
          notify.error('Source and target stores must be different');
          setIsProcessingAction(false);
          return;
        }
        if (currentSourceStock < actionQuantity && !viewingItem.allowNegativeStock) {
          notify.error('Insufficient stock in source store');
          setIsProcessingAction(false);
          return;
        }

        const currentTargetStock = viewingItem.storeStock?.[targetStoreId] || 0;
        const newSourceStock = currentSourceStock - actionQuantity;
        const newTargetStock = currentTargetStock + actionQuantity;

        // Update Item Stock
        await updateDoc(itemRef, {
          [`storeStock.${sourceStoreId}`]: newSourceStock,
          [`storeStock.${targetStoreId}`]: newTargetStock,
          updatedAt: serverTimestamp()
        });

        // Record Movements
        const refNum = `TRF-${Math.random().toString(36).substring(2, 8).toUpperCase()}`;
        
        // Outgoing
        await addDoc(movementsCol, {
          itemId: viewingItem.id,
          storeId: sourceStoreId,
          targetStoreId: targetStoreId,
          type: 'transfer_out',
          orgId: viewingItem.orgId || orgId,
          quantity: -actionQuantity,
          previousStock: currentSourceStock,
          newStock: newSourceStock,
          referenceNumber: refNum,
          note: actionNote,
          createdBy: currentUser?.uid,
          createdByName: users.find(u => u.id === currentUser?.uid)?.displayName || users.find(u => u.id === currentUser?.uid)?.name || currentUser?.email,
          createdAt: serverTimestamp()
        });

        // Incoming
        await addDoc(movementsCol, {
          itemId: viewingItem.id,
          storeId: targetStoreId,
          sourceStoreId: sourceStoreId,
          type: 'transfer_in',
          orgId: viewingItem.orgId || orgId,
          quantity: actionQuantity,
          previousStock: currentTargetStock,
          newStock: newTargetStock,
          referenceNumber: refNum,
          note: actionNote,
          createdBy: currentUser?.uid,
          createdByName: users.find(u => u.id === currentUser?.uid)?.displayName || users.find(u => u.id === currentUser?.uid)?.name || currentUser?.email,
          createdAt: serverTimestamp()
        });

        notify.confirm('Stock transferred successfully');
      } else if (stockAction.type === 'write_off') {
        const newStock = currentSourceStock - actionQuantity;
        
        await updateDoc(itemRef, {
          [`storeStock.${sourceStoreId}`]: newStock,
          updatedAt: serverTimestamp()
        });

        await addDoc(movementsCol, {
          itemId: viewingItem.id,
          storeId: sourceStoreId,
          type: 'write_off',
          orgId: viewingItem.orgId || orgId,
          quantity: -actionQuantity,
          previousStock: currentSourceStock,
          newStock: newStock,
          referenceNumber: `WO-${Date.now().toString().slice(-6)}`,
          note: actionNote,
          createdBy: currentUser?.uid,
          createdByName: users.find(u => u.id === currentUser?.uid)?.displayName || users.find(u => u.id === currentUser?.uid)?.name || currentUser?.email,
          createdAt: serverTimestamp()
        });

        notify.confirm('Stock written off successfully');
      } else if (stockAction.type === 'stock_take') {
        // For stock take, actionQuantity is the NEW TOTAL
        const newStock = actionQuantity;
        const delta = newStock - currentSourceStock;
        
        await updateDoc(itemRef, {
          [`storeStock.${sourceStoreId}`]: newStock,
          updatedAt: serverTimestamp()
        });

        await addDoc(movementsCol, {
          itemId: viewingItem.id,
          storeId: sourceStoreId,
          type: 'single stock take',
          orgId: viewingItem.orgId || orgId,
          quantity: delta,
          previousStock: currentSourceStock,
          newStock: newStock,
          referenceNumber: `ADJ-${Date.now().toString().slice(-6)}`,
          note: actionNote,
          createdBy: currentUser?.uid,
          createdByName: users.find(u => u.id === currentUser?.uid)?.displayName || users.find(u => u.id === currentUser?.uid)?.name || currentUser?.email,
          createdAt: serverTimestamp()
        });

        notify.confirm('Stock adjusted successfully');
      }

      setStockAction({ type: null, storeId: null });
      setActionQuantity(0);
      setActionNote('');
      setTargetStoreId('');
    } catch (error) {
      console.error('Error processing stock action:', error);
      notify.error('Failed to process stock action');
    } finally {
      setIsProcessingAction(false);
    }
  };

  const [creatorName, setCreatorName] = useState<string | null>(null);

  useEffect(() => {
    if (viewingItem?.createdBy && !users.find(u => u.id === viewingItem.createdBy)) {
      if (isLocalSession()) {
        setCreatorName(viewingItem.createdBy);
        return;
      }
      // Try to fetch this specific user if not in the org list
      const userRef = doc(db, 'users', viewingItem.createdBy);
      getDoc(userRef).then(docSnap => {
        if (docSnap.exists()) {
          const data = docSnap.data();
          setCreatorName(data.displayName || data.name || data.email || viewingItem.createdBy);
        } else {
          setCreatorName(viewingItem.createdBy);
        }
      }).catch(() => {
        setCreatorName(viewingItem.createdBy);
      });
    } else {
      setCreatorName(null);
    }
  }, [viewingItem, users]);

  const getUserName = (uid?: string) => {
    if (!uid) return 'Unknown';
    
    // Check local users list first
    const user = users.find(u => u.id === uid);
    if (user) return user.displayName || user.name || user.email;
    
    // Check if we fetched it specifically
    if (creatorName && uid === viewingItem?.createdBy) return creatorName;

    // Check if it's the current user
    if (auth.currentUser?.uid === uid) {
      return auth.currentUser.displayName || auth.currentUser.email;
    }

    // Fallback if uid is actually an email or if user not found yet
    if (uid.includes('@')) return uid.split('@')[0];
    return uid;
  };

  const formatDate = (timestamp: any) => {
    if (!timestamp) return 'N/A';
    const date = timestamp.toDate ? timestamp.toDate() : new Date(timestamp);
    return date.toLocaleString();
  };

  useEffect(() => {
    if (viewingItem?.barcode && barcodeRef.current) {
      try {
        JsBarcode(barcodeRef.current, viewingItem.barcode, {
          format: "CODE128",
          width: 2,
          height: barcodeSettings.barcodeHeight,
          displayValue: barcodeSettings.showCode,
          fontSize: barcodeSettings.fontSize,
          margin: 0
        });
      } catch (error) {
        console.error('Barcode generation error:', error);
      }
    }
  }, [viewingItem, barcodeSettings, isViewModalOpen]);

  const handleGenerateCatalogQr = () => {
    setIsCatalogQrModalOpen(true);
  };

  const handlePrintBarcode = () => {
    const printWindow = window.open('', '_blank');
    if (!printWindow) return;

    // We can't easily get the SVG from qrcode.react in many cases, so we use the URL directly
    // Or we use a hidden canvas/svg to generate it.
    // For this implementation, we will use a template that includes the barcode SVG we already have.
    
    let barcodeSvg = '';
    if (barcodeRef.current) {
       const svgData = new XMLSerializer().serializeToString(barcodeRef.current);
       const svgBase64 = btoa(svgData);
       barcodeSvg = `data:image/svg+xml;base64,${svgBase64}`;
    }

    const qrUrl = `${window.location.origin}/#/menu/product/${viewingItem?.id}`;

    printWindow.document.write(localizePrintableHtml(`
      <html>
        <head>
          <title>Print Label - ${viewingItem?.name}</title>
          <style>
            @page { size: ${barcodeSettings.labelWidth}mm ${barcodeSettings.labelHeight}mm; margin: 0; }
            body { 
              margin: 0; 
              display: flex; 
              flex-direction: column; 
              align-items: center; 
              justify-content: center; 
              height: 100vh;
              font-family: sans-serif;
              text-align: center;
              background: white;
            }
            .label-container {
              width: ${barcodeSettings.labelWidth}mm;
              height: ${barcodeSettings.labelHeight}mm;
              display: grid;
              grid-template-columns: ${barcodeSettings.showQr ? '3fr 2fr' : '1fr'};
              gap: 2mm;
              padding: 2mm;
              box-sizing: border-box;
              overflow: hidden;
            }
            .info-section {
              display: flex;
              flex-direction: column;
              justify-content: center;
              align-items: center;
              text-align: center;
            }
            .product-name { font-size: 7pt; font-weight: bold; line-height: 1.1; margin-bottom: 1mm; max-height: 1.1em; overflow: hidden; }
            .product-price { font-size: 8pt; font-weight: 900; margin-top: 1mm; }
            .barcode-img { max-width: 100%; height: auto; }
            .qr-container {
               display: flex;
               align-items: center;
               justify-content: center;
            }
            .qr-placeholder {
              width: 100%;
              aspect-ratio: 1;
              background: white;
            }
          </style>
          <script src="https://cdn.jsdelivr.net/npm/qrcode@1.5.4/build/qrcode.min.js"></script>
        </head>
        <body>
          <div class="label-container">
            <div class="info-section">
              ${barcodeSettings.showName ? `<div class="product-name">${viewingItem?.name}</div>` : ''}
              ${barcodeSvg ? `<img class="barcode-img" src="${barcodeSvg}" />` : ''}
              ${barcodeSettings.showPrice ? `<div class="product-price">EGP ${(viewingItem?.unitPrice || 0).toLocaleString(undefined, { minimumFractionDigits: 2 })}</div>` : ''}
            </div>
            ${barcodeSettings.showQr ? `
              <div class="qr-container">
                <canvas id="qr-canvas" class="qr-placeholder"></canvas>
              </div>
            ` : ''}
          </div>
          <script>
            window.onload = () => {
              if (document.getElementById('qr-canvas')) {
                QRCode.toCanvas(document.getElementById('qr-canvas'), "${qrUrl}", {
                  margin: 0,
                  width: 100
                }, function() {
                  setTimeout(() => {
                    window.print();
                    setTimeout(() => window.close(), 500);
                  }, 200);
                });
              } else {
                window.print();
                setTimeout(() => window.close(), 500);
              }
            };
          </script>
        </body>
      </html>
    `));
    printWindow.document.close();
  };

  const handleGenerateQr = async (item: Item) => {
    setCurrentQrItem(item);
    setQrLoading(true);
    setIsQrModalOpen(true);
    setQrDataUrl(null);

    try {
      const publicUrl = `${window.location.origin}/#/menu/product/${item.id}`;
      const response = await fetch('/api/admin/qr/generate', {
        method: 'POST',
        headers: { 'Content-Type': 'application/json' },
        body: JSON.stringify({ url: publicUrl })
      });

      const data = await response.json();
      if (data.success) {
        setQrDataUrl(data.qrCode);
      } else {
        notify.error('Failed to generate QR code');
      }
    } catch (error) {
      console.error('QR error:', error);
      notify.error('Failed to connect to QR server');
    } finally {
      setQrLoading(false);
    }
  };

  const downloadQrCode = () => {
    if (!qrDataUrl || !currentQrItem) return;
    const link = document.createElement('a');
    link.href = qrDataUrl;
    link.download = `QR_${currentQrItem.code}_${currentQrItem.name}.png`;
    document.body.appendChild(link);
    link.click();
    document.body.removeChild(link);
  };

  const renderUomSelectors = () => {
    if (!editingItem) return null;
    const stockId = getProductUomId(editingItem, 'stock');
    const compatible = compatibleUoms(uoms, stockId);
    const stockLocked = movementItemIds.has(editingItem.id);
    const changeStock = (id: string) => {
      const stock = resolveUom(uoms, id);
      let next = withProductUom(editingItem, 'stock', stock);
      const allowedIds = new Set(compatibleUoms(uoms, id).map(unit => unit.id));
      if (!allowedIds.has(getProductUomId(next, 'purchase'))) next = withProductUom(next, 'purchase', stock);
      if (!allowedIds.has(getProductUomId(next, 'sales'))) next = withProductUom(next, 'sales', stock);
      setEditingItem(next);
    };
    return (
      <div className="space-y-3 p-4 bg-[#141414]/5 rounded-2xl border border-[#141414]/10">
        <div className="flex items-center justify-between gap-3">
          <div>
            <p className="text-[10px] font-bold uppercase tracking-widest">Product Units</p>
            <p className="text-[10px] opacity-40 mt-1">Purchase and sales units are limited to the stock unit category.</p>
          </div>
          <span className="px-2 py-1 rounded-full bg-[#141414] text-white text-[9px] font-bold uppercase tracking-wider">
            {editingItem.trackInventory ? 'Product • inventory tracked' : 'Product • inventory not tracked'}
          </span>
        </div>
        <div className="grid grid-cols-1 md:grid-cols-3 gap-3">
          <label className="text-[10px] font-bold uppercase tracking-widest opacity-60">Stock UOM
            <select value={stockId} disabled={stockLocked} onChange={event => changeStock(event.target.value)} className="mt-1 w-full px-3 py-2 bg-white rounded-xl outline-none disabled:opacity-50 disabled:cursor-not-allowed">
              <option value="">Select unit</option>
              {compatibleUoms(uoms).map(unit => <option key={unit.id} value={unit.id}>{unit.name} ({unit.category})</option>)}
            </select>
          </label>
          {(['purchase', 'sales'] as const).map(role => <label key={role} className="text-[10px] font-bold uppercase tracking-widest opacity-60">{role} UOM
            <select value={getProductUomId(editingItem, role)} disabled={!stockId} onChange={event => setEditingItem(withProductUom(editingItem, role, resolveUom(uoms, event.target.value)))} className="mt-1 w-full px-3 py-2 bg-white rounded-xl outline-none disabled:opacity-50 disabled:cursor-not-allowed">
              <option value="">Select unit</option>
              {compatible.map(unit => <option key={unit.id} value={unit.id}>{unit.name}</option>)}
            </select>
          </label>)}
        </div>
        {stockLocked && <p className="text-[10px] font-bold text-amber-700">Stock UOM is locked because inventory movements exist for this product.</p>}
      </div>
    );
  };

  const recipeUomOptions = (itemId: string, currentUnit?: string) => {
    const component = items.find(item => item.id === itemId);
    const options = compatibleUoms(uoms, getProductUomId(component, 'stock'));
    return { options, legacy: currentUnit && !resolveUom(uoms, currentUnit) ? currentUnit : '' };
  };

  const isCherry = catalogSettings?.theme === 'cherry';
  const CHERRY_PALETTE = {
    nightBordeaux: '#590D22',
    carkWine: '#800F2F',
    roseRed: '#A4133C',
    deepRose: '#C9184A',
    brightPink: '#FF4D6D',
    pastelPink: '#FF758F',
    softPetal: '#FF85A1',
    lightPink: '#FFB3C1',
    pastelPetal: '#FFCCD5',
    cherryBlossom: '#FFF0F3',
    white: '#FFFFFF'
  };

  return (
    <div className={cn(
      "space-y-6 transition-all duration-700 p-4 rounded-[2.5rem]",
      isCherry && "bg-[#FFF0F3]/50 backdrop-blur-sm border-2 border-double border-[#FFCCD5]"
    )}>
      <div className="flex flex-col md:flex-row md:items-center justify-between gap-4">
        <div className="relative flex-1 max-w-md">
          <Search className={cn("absolute left-3 top-1/2 -translate-y-1/2 w-4 h-4 opacity-40", isCherry && "text-[#590D22]")} />
          <input
            type="text"
            placeholder="Search catalog by name, code or barcode..."
            value={searchQuery}
            onChange={(e) => setSearchQuery(e.target.value)}
            className={cn(
              "w-full pl-10 pr-10 py-2 border transition-all text-sm outline-none",
              isCherry ? "bg-white border-[#FFCCD5] rounded-full focus:border-[#A4133C] text-[#590D22] placeholder:text-[#590D22]/40" : "bg-white border-[#141414]/10 rounded-xl focus:border-[#141414]/30"
            )}
          />
          <div className="absolute right-3 top-1/2 -translate-y-1/2 flex items-center gap-1 opacity-20">
            <BarcodeIcon className={cn("w-4 h-4", isCherry && "text-[#590D22]")} />
            <span className={cn("text-[10px] font-bold uppercase tracking-widest", isCherry && "text-[#590D22]")}>Scan</span>
          </div>
        </div>
        <div className="flex items-center gap-4">
          <div className={cn(
            "flex items-center gap-2 text-xs opacity-50 italic serif",
            isCherry && "text-[#800F2F] opacity-100 font-serif"
          )}>
            <Package className="w-4 h-4" />
            <span>{items.length} POS Enabled Products</span>
          </div>
          <button 
            onClick={handleGenerateCatalogQr}
            className={cn(
              "flex items-center gap-2 px-4 py-2 border transition-all text-sm font-bold shadow-sm group",
              isCherry ? "bg-[#590D22] text-white border-[#590D22] rounded-full hover:bg-[#800F2F]" : "bg-white border-[#141414]/10 rounded-xl hover:border-[#141414]/30"
            )}
          >
            <QrCode className={cn("w-4 h-4", isCherry ? "text-white" : "text-primary")} />
            <span className="hidden sm:inline">Catalog Landing Page QR</span>
            <ArrowUpRight className="w-3 h-3 opacity-0 group-hover:opacity-100 transition-opacity" />
          </button>
        </div>
      </div>

      {loading ? (
        <div className="flex flex-col items-center justify-center py-20 opacity-20">
          <Loader2 className="w-12 h-12 animate-spin mb-4" />
          <p className="italic serif">Loading POS catalog...</p>
        </div>
      ) : filteredItems.length === 0 ? (
        <div className={cn(
          "text-center py-20 opacity-40 italic serif border-2 border-dashed rounded-[2.5rem]",
          isCherry ? "border-[#FFCCD5] bg-white text-[#590D22]" : "border-[#141414]/10"
        )}>
          {searchQuery ? 'No products found matching your search.' : 'No POS-enabled products found. Enable products for POS in the Items module.'}
        </div>
      ) : (
        <div className={cn(
          "grid grid-cols-1 md:grid-cols-2 lg:grid-cols-3 xl:grid-cols-4 gap-4",
          isCherry && "gap-6"
        )}>
          {filteredItems.map((item) => (
            <div 
              key={item.id}
              onClick={() => {
                setViewingItem(item);
                setIsViewModalOpen(true);
              }}
              className={cn(
                "group bg-white border transition-all hover:shadow-lg relative overflow-hidden cursor-pointer",
                isCherry ? "border-[#FFCCD5] rounded-[2rem] hover:border-[#A4133C] p-5 shadow-sm" : "border-[#141414]/10 rounded-2xl p-4 hover:border-[#141414]/30"
              )}
            >
              <div className="flex items-start justify-between mb-3">
                <div className={cn(
                  "w-12 h-12 rounded-xl flex items-center justify-center transition-all",
                  isCherry ? "bg-[#FFF0F3] group-hover:bg-[#FFCCD5]" : "bg-[#141414]/5"
                )}>
                  {item.imageUrl ? (
                    <img src={item.imageUrl} alt={item.name} className="w-full h-full object-cover rounded-xl" referrerPolicy="no-referrer" />
                  ) : (
                    <Package className={cn("w-6 h-6", isCherry ? "text-[#FF85A1]" : "opacity-20")} />
                  )}
                </div>
                <div className="flex gap-1">
                  <button 
                    onClick={(e) => {
                      e.stopPropagation();
                      handleGenerateQr(item);
                    }}
                    className={cn(
                      "p-2 transition-all opacity-0 group-hover:opacity-100",
                      isCherry ? "hover:bg-[#FFF0F3] text-[#A4133C] rounded-full" : "hover:bg-[#141414]/5 rounded-lg"
                    )}
                    title="Generate QR Menu Code"
                  >
                    <QrCode className="w-4 h-4" />
                  </button>
                  <button 
                    onClick={(e) => {
                      e.stopPropagation();
                      setViewingItem(item);
                      setIsViewModalOpen(true);
                    }}
                    className={cn(
                      "p-2 transition-all opacity-0 group-hover:opacity-100",
                      isCherry ? "hover:bg-[#FFF0F3] text-[#A4133C] rounded-full" : "hover:bg-[#141414]/5 rounded-lg"
                    )}
                  >
                    <Eye className="w-4 h-4" />
                  </button>
                  <button 
                    onClick={(e) => {
                      e.stopPropagation();
                      setEditingItem({
                        ...item,
                        barcode: item.barcode || '',
                        category: item.category || '',
                        description: item.description || '',
                        ingredients: item.ingredients || [],
                        imageUrl: item.imageUrl || '',
                        tags: item.tags || [],
                        recipeManagement: !!item.recipeManagement,
                        allowNegativeStock: !!item.allowNegativeStock,
                        hasExpiry: !!item.hasExpiry,
                        expiryDate: item.expiryDate || '',
                        hideFromSellScreen: !!item.hideFromSellScreen
                      });
                      setIsEditModalOpen(true);
                    }}
                    className={cn(
                      "p-2 transition-all opacity-0 group-hover:opacity-100",
                      isCherry ? "hover:bg-[#FFF0F3] text-[#A4133C] rounded-full" : "hover:bg-[#141414]/5 rounded-lg"
                    )}
                  >
                    <Edit2 className="w-4 h-4" />
                  </button>
                </div>
              </div>
              
              <div className="space-y-1">
                <h3 className={cn(
                  "font-bold text-sm truncate",
                  isCherry && "text-[#590D22] font-serif"
                )}>{item.name}</h3>
                <p className={cn(
                  "text-[10px] opacity-40 font-mono uppercase tracking-widest",
                  isCherry && "text-[#800F2F]/50"
                )}>{item.code}</p>
              </div>

              <div className="mt-4 flex items-center justify-between">
                <div className={cn(
                  "text-sm font-bold",
                  isCherry && "text-[#A4133C]"
                )}>
                  EGP {(item.unitPrice || 0).toLocaleString(undefined, { minimumFractionDigits: 2 })}
                </div>
                {item.barcode && (
                  <div className="flex items-center gap-1 text-[10px] opacity-40">
                    <Tag className="w-3 h-3" />
                    <span>{item.barcode}</span>
                  </div>
                )}
              </div>
            </div>
          ))}
        </div>
      )}

      {/* QR Code Modal */}
      {isQrModalOpen && (
        <div
          className="fixed inset-0 bg-[#141414]/40 backdrop-blur-sm z-[200] flex items-center justify-center overflow-y-auto overscroll-contain p-4"
          onMouseDown={(event) => {
            if (event.target === event.currentTarget) setIsQrModalOpen(false);
          }}
        >
          <div className="bg-white w-full max-w-sm rounded-[2.5rem] shadow-2xl overflow-hidden animate-in zoom-in-95 duration-200">
            <div className="p-8 border-b border-[#141414]/5 flex items-center justify-between">
              <div>
                <h2 className="text-xl font-bold italic serif">Digital Menu QR</h2>
                <p className="text-[10px] opacity-40 uppercase tracking-widest">{currentQrItem?.name}</p>
              </div>
              <button 
                onClick={() => setIsQrModalOpen(false)}
                className="p-2 hover:bg-[#141414]/5 rounded-full transition-colors"
              >
                <X className="w-5 h-5" />
              </button>
            </div>
            
            <div className="p-8 flex flex-col items-center gap-6">
              <div className="w-64 h-64 bg-[#141414]/5 rounded-3xl flex items-center justify-center border-2 border-dashed border-[#141414]/10 relative overflow-hidden">
                {qrLoading ? (
                  <Loader2 className="w-10 h-10 animate-spin opacity-20" />
                ) : qrDataUrl ? (
                  <img src={qrDataUrl} alt="QR Code" className="w-full h-full object-contain p-4" />
                ) : (
                  <AlertCircle className="w-10 h-10 opacity-20" />
                )}
              </div>
              
              <div className="text-center space-y-1">
                <p className="text-xs font-bold text-[#141414]/60">Scan to view Digital Menu</p>
                <p className="text-[10px] opacity-30 serif italic">High resolution PNG format</p>
              </div>

              <div className="flex w-full gap-3">
                <button
                  onClick={() => setIsQrModalOpen(false)}
                  className="flex-1 py-4 rounded-2xl font-bold hover:bg-[#141414]/5 transition-colors text-sm"
                >
                  Close
                </button>
                <button
                  onClick={downloadQrCode}
                  disabled={!qrDataUrl}
                  className="flex-[2] py-4 bg-[#141414] text-white rounded-2xl font-bold hover:opacity-90 transition-all flex items-center justify-center gap-2 disabled:opacity-50 text-sm"
                >
                  <Printer className="w-4 h-4" />
                  Download QR
                </button>
              </div>
            </div>
          </div>
        </div>
      )}

      {/* View Details Modal */}
      {isViewModalOpen && viewingItem && (
        <div
          className="fixed inset-0 bg-[#141414]/40 backdrop-blur-sm z-[100] flex items-center justify-center overflow-y-auto overscroll-contain p-4"
          onMouseDown={(event) => {
            if (event.target === event.currentTarget) setIsViewModalOpen(false);
          }}
        >
          <div className="bg-white w-full max-w-4xl max-h-[90vh] rounded-[2.5rem] shadow-2xl overflow-hidden animate-in zoom-in-95 duration-200 flex flex-col">
            <div className="p-8 border-b border-[#141414]/5 flex items-center justify-between shrink-0">
              <div className="flex items-center gap-4">
                <div className="w-16 h-16 rounded-2xl bg-[#141414]/5 flex items-center justify-center">
                  {viewingItem.imageUrl ? (
                    <img src={viewingItem.imageUrl} alt={viewingItem.name} className="w-full h-full object-cover rounded-2xl" referrerPolicy="no-referrer" />
                  ) : (
                    <Package className="w-8 h-8 opacity-20" />
                  )}
                </div>
                <div>
                  <h2 className="text-2xl font-bold italic serif">{viewingItem.name}</h2>
                  <p className="text-xs opacity-40 uppercase tracking-widest">{viewingItem.code}</p>
                </div>
              </div>
              <div className="flex items-center gap-2">
                <button 
                  onClick={() => {
                    setEditingItem({
                      ...viewingItem,
                      barcode: viewingItem.barcode || '',
                      category: viewingItem.category || '',
                      description: viewingItem.description || '',
                      imageUrl: viewingItem.imageUrl || '',
                      tags: viewingItem.tags || [],
                      recipeManagement: !!viewingItem.recipeManagement,
                      allowNegativeStock: !!viewingItem.allowNegativeStock,
                      hasExpiry: !!viewingItem.hasExpiry,
                      expiryDate: viewingItem.expiryDate || '',
                      hideFromSellScreen: !!viewingItem.hideFromSellScreen
                    });
                    setIsEditModalOpen(true);
                  }}
                  className="flex items-center gap-2 px-4 py-2 bg-[#141414]/5 hover:bg-[#141414]/10 rounded-xl text-sm font-bold transition-colors"
                >
                  <Edit2 className="w-4 h-4" />
                  Edit Product
                </button>
                <button 
                  onClick={() => setIsViewModalOpen(false)}
                  className="p-2 hover:bg-[#141414]/5 rounded-full transition-colors"
                >
                  <X className="w-6 h-6" />
                </button>
              </div>
            </div>

            <div className="flex-1 overflow-y-auto p-8 space-y-8 custom-scrollbar">
              {/* Basic Info Grid */}
              <div className="grid grid-cols-1 md:grid-cols-2 lg:grid-cols-3 gap-8">
                {/* Left Column: Details & Pricing */}
                <div className="space-y-8">
                  <div className="space-y-4">
                    <h3 className="text-[10px] font-bold uppercase tracking-widest opacity-40 flex items-center gap-2">
                      <Info className="w-3 h-3" />
                      Product Details
                    </h3>
                    <div className="space-y-3">
                      <div className="flex justify-between text-sm">
                        <span className="opacity-40">Barcode</span>
                        <span className="font-bold">{viewingItem.barcode || 'N/A'}</span>
                      </div>
                      <div className="flex justify-between text-sm">
                        <span className="opacity-40">Category / Family</span>
                        <span className="font-bold">{viewingItem.category || 'N/A'}</span>
                      </div>
                      <div className="flex justify-between text-sm">
                        <span className="opacity-40">Product ID</span>
                        <span className="font-mono text-[10px] bg-[#141414]/5 px-2 py-0.5 rounded">{viewingItem.id}</span>
                      </div>
                    </div>
                  </div>

                  <div className="space-y-4">
                    <h3 className="text-[10px] font-bold uppercase tracking-widest opacity-40 flex items-center gap-2">
                      <Tag className="w-3 h-3" />
                      Pricing
                    </h3>
                    <div className="space-y-3">
                      <div className="flex justify-between text-sm">
                        <span className="opacity-40">Cost Price</span>
                        <span className="font-bold">EGP {(viewingItem.costPrice || 0).toLocaleString(undefined, { minimumFractionDigits: 2 })}</span>
                      </div>
                      <div className="flex justify-between text-sm">
                        <span className="opacity-40">Sell Price</span>
                        <span className="font-bold text-green-600">EGP {(viewingItem.unitPrice || 0).toLocaleString(undefined, { minimumFractionDigits: 2 })}</span>
                      </div>
                      <div className="flex justify-between text-sm">
                        <span className="opacity-40">Margin</span>
                        <span className="font-bold">
                          {viewingItem.costPrice ? `${((((viewingItem.unitPrice || 0) - viewingItem.costPrice) / (viewingItem.unitPrice || 1)) * 100).toFixed(1)}%` : 'N/A'}
                        </span>
                      </div>
                    </div>
                  </div>
                </div>

                {/* Middle Column: Barcode Label Management */}
                <div className="space-y-4 bg-[#141414]/5 p-6 rounded-[2rem] border border-[#141414]/5">
                  <div className="flex items-center justify-between mb-2">
                    <h3 className="text-[10px] font-bold uppercase tracking-widest opacity-40 flex items-center gap-2">
                      <BarcodeIcon className="w-3 h-3" />
                      Barcode Label
                    </h3>
                    <button 
                      onClick={handlePrintBarcode}
                      disabled={!viewingItem.barcode}
                      className="p-2 bg-[#141414] text-white rounded-xl hover:opacity-90 transition-all disabled:opacity-20"
                    >
                      <Printer className="w-4 h-4" />
                    </button>
                  </div>

                  {viewingItem.barcode ? (
                    <div className="space-y-6">
                      <div className="bg-white p-4 rounded-xl border border-[#141414]/10 flex flex-col items-center justify-center min-h-[120px]">
                        {barcodeSettings.showName && (
                          <div className="text-[10px] font-bold mb-1 truncate w-full text-center">{viewingItem.name}</div>
                        )}
                        <svg ref={barcodeRef} className="max-w-full h-auto"></svg>
                        {barcodeSettings.showPrice && (
                          <div className="text-xs font-bold mt-1">EGP {(viewingItem.unitPrice || 0).toLocaleString(undefined, { minimumFractionDigits: 2 })}</div>
                        )}
                      </div>

                      <div className="space-y-3">
                        <h4 className="text-[9px] font-bold uppercase tracking-widest opacity-40">Label Settings</h4>
                        <div className="grid grid-cols-2 gap-2">
                          <button 
                            onClick={() => setBarcodeSettings(prev => ({ ...prev, showName: !prev.showName }))}
                            className={cn("px-3 py-2 rounded-lg text-[10px] font-bold border transition-all", barcodeSettings.showName ? "bg-[#141414] text-white border-[#141414]" : "bg-white border-[#141414]/10 opacity-60")}
                          >
                            Show Name
                          </button>
                          <button 
                            onClick={() => setBarcodeSettings(prev => ({ ...prev, showPrice: !prev.showPrice }))}
                            className={cn("px-3 py-2 rounded-lg text-[10px] font-bold border transition-all", barcodeSettings.showPrice ? "bg-[#141414] text-white border-[#141414]" : "bg-white border-[#141414]/10 opacity-60")}
                          >
                            Show Price
                          </button>
                          <button 
                            onClick={() => setBarcodeSettings(prev => ({ ...prev, showCode: !prev.showCode }))}
                            className={cn("px-3 py-2 rounded-lg text-[10px] font-bold border transition-all", barcodeSettings.showCode ? "bg-[#141414] text-white border-[#141414]" : "bg-white border-[#141414]/10 opacity-60")}
                          >
                            Show Code
                          </button>
                          <div className="flex items-center gap-2 bg-white px-3 py-2 rounded-lg border border-[#141414]/10">
                            <Layout className="w-3 h-3 opacity-40" />
                            <select 
                              value={barcodeSettings.barcodeHeight}
                              onChange={(e) => setBarcodeSettings(prev => ({ ...prev, barcodeHeight: Number(e.target.value) }))}
                              className="bg-transparent text-[10px] font-bold outline-none w-full"
                            >
                              <option value={30}>Short</option>
                              <option value={40}>Medium</option>
                              <option value={60}>Tall</option>
                            </select>
                          </div>
                        </div>
                      </div>
                    </div>
                  ) : (
                    <div className="flex flex-col items-center justify-center py-10 opacity-20 italic serif text-center">
                      <BarcodeIcon className="w-10 h-10 mb-2" />
                      <p className="text-xs">No barcode assigned to this product.</p>
                    </div>
                  )}
                </div>

                {/* Right Column: System Info & Flags */}
                <div className="space-y-8">
                  <div className="space-y-4">
                    <h3 className="text-[10px] font-bold uppercase tracking-widest opacity-40 flex items-center gap-2">
                      <Settings className="w-3 h-3" />
                      System Info
                    </h3>
                    <div className="space-y-3">
                      <div className="flex justify-between text-sm">
                        <span className="opacity-40">Created By</span>
                        <span className="font-bold flex items-center gap-1">
                          <UserIcon className="w-3 h-3" />
                          {getUserName(viewingItem.createdBy)}
                        </span>
                      </div>
                      <div className="flex justify-between text-sm">
                        <span className="opacity-40">Created At</span>
                        <span className="font-bold flex items-center gap-1">
                          <Clock className="w-3 h-3" />
                          {formatDate(viewingItem.createdAt)}
                        </span>
                      </div>
                    </div>
                  </div>

                  <div className="space-y-4">
                    <h3 className="text-[10px] font-bold uppercase tracking-widest opacity-40 flex items-center gap-2">
                      <Layout className="w-3 h-3" />
                      Inventory Rules
                    </h3>
                    <div className="space-y-2">
                      <div className={cn("flex items-center justify-between px-4 py-2 rounded-xl border text-[10px] font-bold", viewingItem.allowNegativeStock ? "bg-red-50 border-red-200 text-red-700" : "bg-green-50 border-green-200 text-green-700")}>
                        <span>Allow Negative Stock</span>
                        <Check className={cn("w-3 h-3", !viewingItem.allowNegativeStock && "hidden")} />
                        {!viewingItem.allowNegativeStock && <X className="w-3 h-3" />}
                      </div>
                      <div className={cn("flex items-center justify-between px-4 py-2 rounded-xl border text-[10px] font-bold", viewingItem.hasExpiry ? "bg-orange-50 border-orange-200 text-orange-700" : "bg-gray-50 border-gray-200 text-gray-700")}>
                        <span>Has Expiry Tracking</span>
                        {viewingItem.hasExpiry ? <Check className="w-3 h-3" /> : <X className="w-3 h-3" />}
                      </div>
                    </div>
                  </div>
                </div>
              </div>

              {/* Store Availability */}
              <div className="space-y-4">
                <h3 className="text-lg font-bold italic serif flex items-center gap-2">
                  <Store className="w-5 h-5" />
                  Store Availability
                </h3>
                <div className="grid grid-cols-1 sm:grid-cols-2 md:grid-cols-3 lg:grid-cols-4 gap-4">
                  {stores.map(store => {
                    const stock = viewingItem.storeStock?.[store.id] || 0;
                    return (
                      <div key={store.id} className="bg-[#141414]/5 p-4 rounded-2xl border border-[#141414]/5 relative group/store">
                        <div className="flex items-start justify-between mb-1">
                          <div className="text-[10px] font-bold uppercase tracking-widest opacity-40 truncate pr-6">{store.name}</div>
                          <div className="relative">
                            <button 
                              onClick={(e) => {
                                e.stopPropagation();
                                setOpenMenuStoreId(openMenuStoreId === store.id ? null : store.id);
                              }}
                              className="p-1 hover:bg-[#141414]/10 rounded-lg transition-colors"
                            >
                              <MoreVertical className="w-4 h-4 opacity-40" />
                            </button>
                            
                            {openMenuStoreId === store.id && (
                              <div className="absolute right-0 top-full mt-1 w-48 bg-white border border-[#141414]/10 rounded-xl shadow-xl z-50 py-1 animate-in fade-in slide-in-from-top-2 duration-200">
                                <button 
                                  onClick={() => {
                                    setStockAction({ type: 'transfer', storeId: store.id });
                                    setOpenMenuStoreId(null);
                                  }}
                                  className="w-full px-4 py-2 text-left text-xs hover:bg-[#141414]/5 flex items-center gap-2"
                                >
                                  <ArrowRightLeft className="w-3.5 h-3.5 opacity-40" />
                                  Transfer Stock
                                </button>
                                <button 
                                  onClick={() => {
                                    setStockAction({ type: 'stock_take', storeId: store.id });
                                    setOpenMenuStoreId(null);
                                  }}
                                  className="w-full px-4 py-2 text-left text-xs hover:bg-[#141414]/5 flex items-center gap-2"
                                >
                                  <PlusCircle className="w-3.5 h-3.5 opacity-40" />
                                  Single Stock Take
                                </button>
                                <button 
                                  onClick={() => {
                                    setStockAction({ type: 'write_off', storeId: store.id });
                                    setOpenMenuStoreId(null);
                                  }}
                                  className="w-full px-4 py-2 text-left text-xs hover:bg-red-50 text-red-600 flex items-center gap-2"
                                >
                                  <Trash2 className="w-3.5 h-3.5 opacity-40" />
                                  Write Off
                                </button>
                              </div>
                            )}
                          </div>
                        </div>
                        <div className={cn("text-xl font-bold", stock <= 0 ? "text-red-600" : "text-[#141414]")}>
                          {stock} <span className="text-xs font-normal opacity-40">units</span>
                        </div>
                      </div>
                    );
                  })}
                  <div className="bg-[#141414] text-white p-4 rounded-2xl">
                    <div className="text-[10px] font-bold uppercase tracking-widest opacity-40 mb-1">Stock on Hand</div>
                    <div className="text-xl font-bold">
                      {Object.values(viewingItem.storeStock || {}).reduce((a, b) => a + b, 0)}
                    </div>
                  </div>
                </div>
              </div>

              {/* Stock Log */}
              <div className="space-y-4">
                <div className="flex flex-col md:flex-row md:items-center justify-between gap-4">
                  <h3 className="text-lg font-bold italic serif flex items-center gap-2">
                    <History className="w-5 h-5" />
                    Stock Log
                  </h3>
                  <div className="flex flex-wrap gap-2">
                    <button 
                      onClick={() => setActiveStoreLogId('all')}
                      className={cn(
                        "px-3 py-1 rounded-full text-[10px] font-bold uppercase tracking-widest transition-all",
                        activeStoreLogId === 'all' ? "bg-[#141414] text-white" : "bg-[#141414]/5 hover:bg-[#141414]/10"
                      )}
                    >
                      All Stores
                    </button>
                    {stores.map(store => (
                      <button 
                        key={store.id}
                        onClick={() => setActiveStoreLogId(store.id)}
                        className={cn(
                          "px-3 py-1 rounded-full text-[10px] font-bold uppercase tracking-widest transition-all",
                          activeStoreLogId === store.id ? "bg-[#141414] text-white" : "bg-[#141414]/5 hover:bg-[#141414]/10"
                        )}
                      >
                        {store.name}
                      </button>
                    ))}
                  </div>
                </div>
                
                <div className="bg-white border border-[#141414]/10 rounded-2xl overflow-hidden">
                  <table className="w-full text-left border-collapse">
                    <thead>
                      <tr className="bg-[#141414]/5 border-b border-[#141414]/10">
                        <th className="p-4 text-[10px] font-bold uppercase tracking-widest opacity-40">Date</th>
                        <th className="p-4 text-[10px] font-bold uppercase tracking-widest opacity-40">Type</th>
                        <th className="p-4 text-[10px] font-bold uppercase tracking-widest opacity-40">Store</th>
                        <th className="p-4 text-[10px] font-bold uppercase tracking-widest opacity-40">Change</th>
                        <th className="p-4 text-[10px] font-bold uppercase tracking-widest opacity-40">Stock After</th>
                        <th className="p-4 text-[10px] font-bold uppercase tracking-widest opacity-40">Reference</th>
                        <th className="p-4 text-[10px] font-bold uppercase tracking-widest opacity-40">By</th>
                      </tr>
                    </thead>
                    <tbody className="text-sm">
                      {loadingLog ? (
                        <tr>
                          <td colSpan={7} className="p-8 text-center">
                            <Loader2 className="w-6 h-6 animate-spin mx-auto opacity-20" />
                          </td>
                        </tr>
                      ) : (stockLog.filter(log => activeStoreLogId === 'all' || log.storeId === activeStoreLogId)).length === 0 ? (
                        <tr>
                          <td colSpan={7} className="p-8 text-center opacity-40 italic serif">
                            No stock movements recorded for this view.
                          </td>
                        </tr>
                      ) : (
                        stockLog
                          .filter(log => activeStoreLogId === 'all' || log.storeId === activeStoreLogId)
                          .map((log) => (
                          <tr key={log.id} className="border-b border-[#141414]/5 hover:bg-[#141414]/[0.02] transition-colors">
                            <td className="p-4 whitespace-nowrap opacity-60">{formatDate(log.createdAt)}</td>
                            <td className="p-4">
                              <span className={cn(
                                "px-2 py-1 rounded text-[10px] font-bold uppercase tracking-widest",
                                log.type === 'sale' ? "bg-red-100 text-red-700" :
                                log.type === 'purchase' ? "bg-green-100 text-green-700" :
                                log.type === 'return' ? "bg-blue-100 text-blue-700" :
                                log.type === 'transfer_in' ? "bg-purple-100 text-purple-700" :
                                log.type === 'transfer_out' ? "bg-orange-100 text-orange-700" :
                                log.type === 'write_off' ? "bg-red-100 text-red-700" :
                                log.type === 'single stock take' ? "bg-blue-100 text-blue-700" :
                                "bg-gray-100 text-gray-700"
                              )}>
                                {String(log?.type || '').replace('_', ' ')}
                              </span>
                            </td>
                            <td className="p-4 font-medium">{stores.find(s => s.id === log.storeId)?.name || 'Unknown'}</td>
                            <td className="p-4">
                              <div className={cn("flex items-center gap-1 font-bold", log.quantity > 0 ? "text-green-600" : "text-red-600")}>
                                {log.quantity > 0 ? <ArrowUpRight className="w-3 h-3" /> : <ArrowDownLeft className="w-3 h-3" />}
                                {Math.abs(log.quantity)}
                              </div>
                            </td>
                            <td className="p-4 font-bold">{log.newStock}</td>
                            <td className="p-4">
                              <div className="flex flex-col">
                                <span className="font-bold text-xs">{log.referenceNumber}</span>
                                {log.note && <span className="text-[10px] opacity-40 truncate max-w-[150px]">{log.note}</span>}
                              </div>
                            </td>
                            <td className="p-4 opacity-60">{log.createdByName}</td>
                          </tr>
                        ))
                      )}
                    </tbody>
                  </table>
                </div>
              </div>
            </div>
          </div>
        </div>
      )}

      {/* Stock Action Modal */}
      {stockAction.type && (
        <div
          className="fixed inset-0 bg-[#141414]/40 backdrop-blur-sm z-[110] flex items-center justify-center overflow-y-auto overscroll-contain p-4"
          onMouseDown={(event) => {
            if (event.target === event.currentTarget) setStockAction({ type: null, storeId: null });
          }}
        >
          <div className="bg-white w-full max-w-md max-h-[calc(100dvh-2rem)] overflow-y-auto overscroll-contain rounded-[2.5rem] shadow-2xl animate-in zoom-in-95 duration-200">
            <div className="p-8 border-b border-[#141414]/5 flex items-center justify-between">
              <div>
                <h2 className="text-2xl font-bold italic serif">
                  {stockAction.type === 'transfer' ? 'Transfer Stock' : 
                   stockAction.type === 'write_off' ? 'Write Off Stock' : 'Single Stock Take'}
                </h2>
                <p className="text-xs opacity-40 uppercase tracking-widest">
                  {stores.find(s => s.id === stockAction.storeId)?.name}
                </p>
              </div>
              <button 
                onClick={() => setStockAction({ type: null, storeId: null })}
                className="p-2 hover:bg-[#141414]/5 rounded-full transition-colors"
              >
                <X className="w-6 h-6" />
              </button>
            </div>

            <form onSubmit={handleStockAction} className="p-8 space-y-6">
              <div className="space-y-4">
                {stockAction.type === 'transfer' && (
                  <div className="space-y-2">
                    <label className="text-[10px] font-bold uppercase tracking-widest opacity-40 ml-4 mb-2 block">Target Store</label>
                    <select
                      required
                      value={targetStoreId}
                      onChange={(e) => setTargetStoreId(e.target.value)}
                      className="w-full px-6 py-3 bg-[#141414]/5 rounded-2xl outline-none focus:bg-[#141414]/10 transition-all font-bold appearance-none"
                    >
                      <option value="">Select target store...</option>
                      {stores.filter(s => s.id !== stockAction.storeId).map(store => (
                        <option key={store.id} value={store.id}>{store.name}</option>
                      ))}
                    </select>
                  </div>
                )}

                <div className="space-y-2">
                  <label className="text-[10px] font-bold uppercase tracking-widest opacity-40 ml-4 mb-2 block">
                    {stockAction.type === 'stock_take' ? 'New Total Stock' : 'Quantity'}
                  </label>
                  <input
                    type="number"
                    required
                    value={actionQuantity}
                    onChange={(e) => setActionQuantity(Number(e.target.value))}
                    className="w-full px-6 py-3 bg-[#141414]/5 rounded-2xl outline-none focus:bg-[#141414]/10 transition-all font-bold"
                  />
                </div>

                <div className="space-y-2">
                  <label className="text-[10px] font-bold uppercase tracking-widest opacity-40 ml-4 mb-2 block">Note / Reason</label>
                  <textarea
                    rows={3}
                    value={actionNote}
                    onChange={(e) => setActionNote(e.target.value)}
                    placeholder="Enter reason for this action..."
                    className="w-full px-6 py-3 bg-[#141414]/5 rounded-2xl outline-none focus:bg-[#141414]/10 transition-all font-medium resize-none"
                  />
                </div>
              </div>

              <div className="flex gap-3 pt-4">
                <button
                  type="button"
                  onClick={() => setStockAction({ type: null, storeId: null })}
                  className="flex-1 px-6 py-4 rounded-2xl font-bold hover:bg-[#141414]/5 transition-all"
                >
                  Cancel
                </button>
                <button
                  type="submit"
                  disabled={isProcessingAction}
                  className="flex-[2] px-6 py-4 bg-[#141414] text-white rounded-2xl font-bold hover:opacity-90 transition-all flex items-center justify-center gap-2 disabled:opacity-50"
                >
                  {isProcessingAction ? <Loader2 className="w-5 h-5 animate-spin" /> : <Check className="w-5 h-5" />}
                  {stockAction.type === 'transfer' ? 'Confirm Transfer' : 
                   stockAction.type === 'write_off' ? 'Confirm Write Off' : 'Apply Adjustment'}
                </button>
              </div>
            </form>
          </div>
        </div>
      )}

      {/* Edit Modal */}
      {isEditModalOpen && editingItem && (
        <div
          className="fixed inset-0 bg-[#141414]/40 backdrop-blur-sm z-[100] flex items-center justify-center overflow-y-auto overscroll-contain p-4"
          onMouseDown={(event) => {
            if (event.target === event.currentTarget) setIsEditModalOpen(false);
          }}
        >
          <div className="bg-white w-full max-w-2xl max-h-[90vh] rounded-[2.5rem] shadow-2xl overflow-hidden animate-in zoom-in-95 duration-200 flex flex-col">
            <div className="p-8 border-b border-[#141414]/5 flex items-center justify-between shrink-0">
              <div>
                <h2 className="text-2xl font-bold italic serif">Edit POS Product</h2>
                <p className="text-xs opacity-40 mt-1 uppercase tracking-widest">{`Update details for ${editingItem.code}`}</p>
              </div>
              <button 
                onClick={() => setIsEditModalOpen(false)}
                className="p-2 hover:bg-[#141414]/5 rounded-full transition-colors"
              >
                <X className="w-6 h-6" />
              </button>
            </div>

            <form onSubmit={handleUpdateItem} className="flex-1 overflow-y-auto p-8 space-y-8 custom-scrollbar">
              {/* Basic Info */}
              <div className="space-y-4">
                <h3 className="text-[10px] font-bold uppercase tracking-widest opacity-40 flex items-center gap-2">
                  <Info className="w-3 h-3" />
                  Basic Information
                </h3>
                <div className="grid grid-cols-1 md:grid-cols-2 gap-4">
                  <div className="md:col-span-2">
                    <label className="text-[10px] font-bold uppercase tracking-widest opacity-40 ml-4 mb-2 block">Product Name</label>
                    <input
                      type="text"
                      required
                      value={editingItem.name || ''}
                      onChange={(e) => setEditingItem({ ...editingItem, name: e.target.value })}
                      className="w-full px-6 py-3 bg-[#141414]/5 rounded-2xl outline-none focus:bg-[#141414]/10 transition-all font-bold"
                    />
                  </div>

                  {/* Image Upload below Name */}
                  <div className="md:col-span-2">
                    <label className="text-[10px] font-bold uppercase tracking-widest opacity-40 ml-4 mb-2 block">Product Image (Max 3MB)</label>
                    <div className="flex items-center gap-4 p-4 bg-[#141414]/5 rounded-2xl border-2 border-dashed border-[#141414]/10">
                      <div className="w-20 h-20 rounded-xl bg-white border border-[#141414]/10 overflow-hidden flex items-center justify-center shrink-0">
                        {editingItem.imageUrl ? (
                          <img src={editingItem.imageUrl} alt="Preview" className="w-full h-full object-cover" referrerPolicy="no-referrer" />
                        ) : (
                          <ImageIcon className="w-8 h-8 opacity-20" />
                        )}
                      </div>
                      <div className="flex-1 space-y-2">
                        <input
                          type="file"
                          accept="image/*"
                          onChange={handleImageUpload}
                          className="hidden"
                          id="product-image-upload"
                        />
                        <label
                          htmlFor="product-image-upload"
                          className="inline-flex items-center gap-2 px-4 py-2 bg-[#141414] text-white rounded-xl text-xs font-bold cursor-pointer hover:opacity-90 transition-all"
                        >
                          {isUploading ? <Loader2 className="w-3 h-3 animate-spin" /> : <PlusCircle className="w-3 h-3" />}
                          {isUploading ? (uploadProgress !== null ? `Uploading ${uploadProgress}%` : 'Uploading...') : (editingItem.imageUrl ? 'Change Image' : 'Upload Image')}
                        </label>
                        <p className="text-[10px] opacity-40">Supported formats: JPG, PNG, WEBP. Max size 3MB.</p>
                      </div>
                    </div>
                  </div>

                  <div>
                    <label className="text-[10px] font-bold uppercase tracking-widest opacity-40 ml-4 mb-2 block">Category</label>
                    <select
                      value={editingItem.category || ''}
                      onChange={(e) => setEditingItem({ ...editingItem, category: e.target.value })}
                      className="w-full px-6 py-3 bg-[#141414]/5 rounded-2xl outline-none focus:bg-[#141414]/10 transition-all font-bold appearance-none cursor-pointer"
                    >
                      <option value="">Select Category</option>
                      {categories.map(cat => (
                        <option key={cat} value={cat}>{cat}</option>
                      ))}
                    </select>
                  </div>

                  {/* Description below Category */}
                  <div className="md:col-span-2">
                    <label className="text-[10px] font-bold uppercase tracking-widest opacity-40 ml-4 mb-2 block">Description</label>
                    <textarea
                      rows={2}
                      value={editingItem.description || ''}
                      onChange={(e) => setEditingItem({ ...editingItem, description: e.target.value })}
                      className="w-full px-6 py-3 bg-[#141414]/5 rounded-2xl outline-none focus:bg-[#141414]/10 transition-all font-bold resize-none"
                    />
                  </div>

                  {/* Ingredients below Description */}
                  <div className="md:col-span-2">
                    <label className="text-[10px] font-bold uppercase tracking-widest opacity-40 ml-4 mb-2 block">Ingredients / Components (comma separated)</label>
                    <textarea
                      rows={2}
                      value={editingItem.ingredients?.join(', ') || ''}
                      onChange={(e) => setEditingItem({ ...editingItem, ingredients: e.target.value.split(',').map(t => t.trim()).filter(t => t) })}
                      placeholder="e.g. Flour, Sugar, Milk"
                      className="w-full px-6 py-3 bg-[#141414]/5 rounded-2xl outline-none focus:bg-[#141414]/10 transition-all font-bold resize-none"
                    />
                  </div>

                  <div>
                    <label className="text-[10px] font-bold uppercase tracking-widest opacity-40 ml-4 mb-2 block">Barcode</label>
                    <input
                      type="text"
                      value={editingItem.barcode || ''}
                      onChange={(e) => setEditingItem({ ...editingItem, barcode: e.target.value })}
                      className="w-full px-6 py-3 bg-[#141414]/5 rounded-2xl outline-none focus:bg-[#141414]/10 transition-all font-bold"
                    />
                  </div>
                </div>
              </div>

              {/* Pricing */}
              <div className="space-y-4">
                <h3 className="text-[10px] font-bold uppercase tracking-widest opacity-40 flex items-center gap-2">
                  <Tag className="w-3 h-3" />
                  Pricing
                </h3>
                <div className="grid grid-cols-1 md:grid-cols-2 gap-4">
                  <div>
                    <label className="text-[10px] font-bold uppercase tracking-widest opacity-40 ml-4 mb-2 block">Sale Price (EGP)</label>
                    <input
                      type="number"
                      step="0.01"
                      required
                      value={editingItem.unitPrice || 0}
                      onChange={(e) => setEditingItem({ ...editingItem, unitPrice: Number(e.target.value) })}
                      className="w-full px-6 py-3 bg-[#141414]/5 rounded-2xl outline-none focus:bg-[#141414]/10 transition-all font-bold"
                    />
                  </div>
                  <div>
                    <label className="text-[10px] font-bold uppercase tracking-widest opacity-40 ml-4 mb-2 block">Cost Price (EGP)</label>
                    <input
                      type="number"
                      step="0.01"
                      value={editingItem.costPrice || 0}
                      onChange={(e) => setEditingItem({ ...editingItem, costPrice: Number(e.target.value) })}
                      className="w-full px-6 py-3 bg-[#141414]/5 rounded-2xl outline-none focus:bg-[#141414]/10 transition-all font-bold"
                    />
                  </div>
                </div>
              </div>

              {renderUomSelectors()}

              {/* POS Modules */}
              <div className="space-y-4">
                <h3 className="text-[10px] font-bold uppercase tracking-widest opacity-40 flex items-center gap-2">
                  <Layers className="w-3 h-3" />
                  POS Modules & Settings
                </h3>
                <div className="grid grid-cols-1 md:grid-cols-2 gap-6">
                  {/* Toggles */}
                  <div className="space-y-4">
                    <label className="flex items-center gap-3 p-4 bg-[#141414]/5 rounded-2xl cursor-pointer hover:bg-[#141414]/10 transition-all">
                      <input
                        type="checkbox"
                        checked={!!editingItem.trackInventory}
                        onChange={(e) => setEditingItem({ ...editingItem, trackInventory: e.target.checked })}
                        className="w-5 h-5 rounded border-[#141414]/20 text-[#141414] focus:ring-[#141414]"
                      />
                      <div>
                        <span className="text-sm font-bold">Track Inventory (product)</span>
                        <span className="block text-[10px] opacity-40 mt-0.5">Creates stock movements; services never affect stock.</span>
                      </div>
                    </label>
                    <label className="flex items-center gap-3 p-4 bg-[#141414]/5 rounded-2xl cursor-pointer hover:bg-[#141414]/10 transition-all">
                      <input 
                        type="checkbox" 
                        checked={!!editingItem.allowNegativeStock}
                        onChange={(e) => setEditingItem({ ...editingItem, allowNegativeStock: e.target.checked })}
                        className="w-5 h-5 rounded border-[#141414]/20 text-[#141414] focus:ring-[#141414]"
                      />
                      <div className="flex items-center gap-2">
                        <MinusCircle className="w-4 h-4 opacity-40" />
                        <span className="text-sm font-bold">Allow Negative Stock</span>
                      </div>
                    </label>

                    <label className="flex items-center gap-3 p-4 bg-[#141414]/5 rounded-2xl cursor-pointer hover:bg-[#141414]/10 transition-all">
                      <input 
                        type="checkbox" 
                        checked={!!editingItem.hideFromSellScreen}
                        onChange={(e) => setEditingItem({ ...editingItem, hideFromSellScreen: e.target.checked })}
                        className="w-5 h-5 rounded border-[#141414]/20 text-[#141414] focus:ring-[#141414]"
                      />
                      <div className="flex items-center gap-2">
                        <EyeOff className="w-4 h-4 opacity-40" />
                        <span className="text-sm font-bold">Hide from Sell Screen</span>
                      </div>
                    </label>
                  </div>

                  {/* Expiry & Tags */}
                  <div className="space-y-4">
                    <div className="p-4 bg-[#141414]/5 rounded-2xl space-y-3">
                      <label className="flex items-center gap-3 cursor-pointer">
                        <input 
                          type="checkbox" 
                          checked={!!editingItem.hasExpiry}
                          onChange={(e) => setEditingItem({ ...editingItem, hasExpiry: e.target.checked })}
                          className="w-5 h-5 rounded border-[#141414]/20 text-[#141414] focus:ring-[#141414]"
                        />
                        <div className="flex items-center gap-2">
                          <Calendar className="w-4 h-4 opacity-40" />
                          <span className="text-sm font-bold">Has Expiry Date</span>
                        </div>
                      </label>
                      {editingItem.hasExpiry && (
                        <input 
                          type="date"
                          value={editingItem.expiryDate || ''}
                          onChange={(e) => setEditingItem({ ...editingItem, expiryDate: e.target.value })}
                          className="w-full px-4 py-2 bg-white rounded-xl outline-none text-sm font-bold"
                        />
                      )}
                    </div>

                    <div className="space-y-2">
                      <label className="text-[10px] font-bold uppercase tracking-widest opacity-40 ml-4 block">Tags (comma separated)</label>
                      <div className="relative">
                        <Hash className="absolute left-4 top-1/2 -translate-y-1/2 w-4 h-4 opacity-40" />
                        <input
                          type="text"
                          value={editingItem.tags?.join(', ') || ''}
                          onChange={(e) => setEditingItem({ ...editingItem, tags: e.target.value.split(',').map(t => t.trim()).filter(t => t) })}
                          placeholder="e.g. New, Popular, Sale"
                          className="w-full pl-12 pr-6 py-3 bg-[#141414]/5 rounded-2xl outline-none focus:bg-[#141414]/10 transition-all font-bold"
                        />
                      </div>
                    </div>
                  </div>
                </div>

                {/* Recipe Management */}
                <div className="space-y-4">
                  <label className="flex items-center gap-3 p-4 bg-[#141414]/5 rounded-2xl cursor-pointer hover:bg-[#141414]/10 transition-all">
                    <input 
                      type="checkbox" 
                      checked={!!editingItem.recipeManagement}
                      onChange={(e) => setEditingItem({ ...editingItem, recipeManagement: e.target.checked, recipes: e.target.checked ? (editingItem.recipes || []) : [] })}
                      className="w-5 h-5 rounded border-[#141414]/20 text-[#141414] focus:ring-[#141414]"
                    />
                    <div className="flex items-center gap-2">
                      <ClipboardList className="w-4 h-4 opacity-40" />
                      <span className="text-sm font-bold">Recipe Management</span>
                    </div>
                  </label>

                  {editingItem.recipeManagement && (
                    <div className="space-y-4 p-4 bg-[#141414]/5 rounded-2xl border border-[#141414]/10 animate-in fade-in slide-in-from-top-2">
                      <div className="flex items-center justify-between">
                        <h4 className="text-[10px] font-bold uppercase tracking-widest opacity-40">Recipes</h4>
                        <button 
                          type="button"
                          onClick={() => setEditingItem({
                            ...editingItem,
                            recipes: [...(editingItem.recipes || []), { id: Math.random().toString(36).substr(2, 9), name: '', isActive: (editingItem.recipes?.length || 0) === 0, components: [] }]
                          })}
                          className="text-[10px] font-bold underline hover:opacity-70"
                        >
                          + Add New Recipe
                        </button>
                      </div>
                      <div className="space-y-4">
                        {editingItem.recipes?.map((recipe, rIdx) => (
                          <div key={recipe.id} className="p-4 bg-white rounded-xl border border-[#141414]/10 space-y-4">
                            <div className="flex items-center justify-between gap-4">
                              <div className="flex-1 flex items-center gap-4">
                                <input 
                                  type="text"
                                  placeholder="Recipe Name (e.g. Standard, Premium)"
                                  value={recipe.name}
                                  onChange={(e) => setEditingItem({
                                    ...editingItem,
                                    recipes: editingItem.recipes?.map((r, i) => i === rIdx ? { ...r, name: e.target.value } : r)
                                  })}
                                  className="flex-1 bg-[#141414]/5 border border-[#141414]/10 p-2 rounded-lg text-xs outline-none font-bold"
                                />
                                <div className="flex items-center gap-2">
                                  <input 
                                    type="checkbox"
                                    id={`recipe-active-${rIdx}`}
                                    checked={recipe.isActive}
                                    onChange={(e) => {
                                      if (e.target.checked) {
                                        setEditingItem({
                                          ...editingItem,
                                          recipes: editingItem.recipes?.map((r, i) => ({ ...r, isActive: i === rIdx }))
                                        });
                                      }
                                    }}
                                    className="w-4 h-4 rounded border-[#141414]/20 text-[#141414] focus:ring-[#141414]"
                                  />
                                  <label htmlFor={`recipe-active-${rIdx}`} className="text-[10px] font-bold uppercase tracking-widest opacity-40 cursor-pointer">Active</label>
                                </div>
                              </div>
                              <button 
                                type="button"
                                onClick={() => setEditingItem({ ...editingItem, recipes: editingItem.recipes?.filter((_, i) => i !== rIdx) })}
                                className="p-1 text-red-500 hover:bg-red-50 rounded transition-colors"
                              >
                                <XCircle className="w-4 h-4" />
                              </button>
                            </div>

                            <div className="space-y-2">
                              <div className="flex items-center justify-between">
                                <label className="text-[10px] font-bold uppercase tracking-widest opacity-40">Components</label>
                                <button 
                                  type="button"
                                  onClick={() => setEditingItem({
                                    ...editingItem,
                                    recipes: editingItem.recipes?.map((r, i) => i === rIdx ? { ...r, components: [...r.components, { itemId: '', name: '', quantity: 1, unit: 'pcs' }] } : r)
                                  })}
                                  className="text-[9px] font-bold underline hover:opacity-70"
                                >
                                  + Add Component
                                </button>
                              </div>
                              <div className="space-y-2">
                                {recipe.components.map((ingredient, cIdx) => (
                                  <div key={cIdx} className="flex gap-2 items-start">
                                    <select 
                                      value={ingredient.itemId}
                                      onChange={(e) => {
                                        const selected = items.find(i => i.id === e.target.value);
                                        setEditingItem({
                                          ...editingItem,
                                          recipes: editingItem.recipes?.map((r, i) => i === rIdx ? {
                                            ...r,
                                            components: r.components.map((c, j) => j === cIdx ? { ...c, itemId: e.target.value, name: selected?.name || '' } : c)
                                          } : r)
                                        });
                                      }}
                                      className="flex-1 bg-[#141414]/5 border border-[#141414]/10 p-2 rounded-lg text-[10px] outline-none font-bold"
                                    >
                                      <option value="">Select Item</option>
                                      {items.filter(i => i.id !== editingItem.id).map(i => (
                                        <option key={i.id} value={i.id}>{i.name} ({i.code})</option>
                                      ))}
                                    </select>
                                    <input 
                                      type="number"
                                      placeholder="Qty"
                                      value={isNaN(ingredient.quantity) ? '' : ingredient.quantity}
                                      onChange={(e) => setEditingItem({
                                        ...editingItem,
                                        recipes: editingItem.recipes?.map((r, i) => i === rIdx ? {
                                          ...r,
                                          components: r.components.map((c, j) => j === cIdx ? { ...c, quantity: parseFloat(e.target.value) || 0 } : c)
                                        } : r)
                                      })}
                                      className="w-16 bg-[#141414]/5 border border-[#141414]/10 p-2 rounded-lg text-[10px] outline-none text-center font-bold"
                                    />
                                    <select
                                      value={ingredient.unit || ''}
                                      onChange={(e) => setEditingItem({
                                        ...editingItem,
                                        recipes: editingItem.recipes?.map((r, i) => i === rIdx ? {
                                          ...r,
                                          components: r.components.map((c, j) => j === cIdx ? { ...c, unit: e.target.value } : c)
                                        } : r)
                                      })}
                                      className="w-24 bg-[#141414]/5 border border-[#141414]/10 p-2 rounded-lg text-[10px] outline-none font-bold"
                                    >
                                      <option value="">UOM</option>
                                      {recipeUomOptions(ingredient.itemId, ingredient.unit).legacy && <option value={ingredient.unit}>{ingredient.unit} (legacy)</option>}
                                      {recipeUomOptions(ingredient.itemId, ingredient.unit).options.map(unit => <option key={unit.id} value={unit.name}>{unit.name}</option>)}
                                    </select>
                                    <button 
                                      type="button"
                                      onClick={() => setEditingItem({
                                        ...editingItem,
                                        recipes: editingItem.recipes?.map((r, i) => i === rIdx ? {
                                          ...r,
                                          components: r.components.filter((_, j) => j !== cIdx)
                                        } : r)
                                      })}
                                      className="p-2 text-red-500 hover:bg-red-50 rounded-lg transition-colors"
                                    >
                                      <XCircle className="w-3 h-3" />
                                    </button>
                                  </div>
                                ))}
                              </div>
                            </div>
                          </div>
                        ))}
                      </div>
                    </div>
                  )}
                </div>

                {/* Variants Management */}
                <div className="space-y-4">
                  <label className="flex items-center gap-3 p-4 bg-[#141414]/5 rounded-2xl cursor-pointer hover:bg-[#141414]/10 transition-all">
                    <input 
                      type="checkbox" 
                      checked={!!editingItem.hasVariants}
                      onChange={(e) => setEditingItem({ ...editingItem, hasVariants: e.target.checked, variantOptions: e.target.checked ? (editingItem.variantOptions || []) : [], variants: e.target.checked ? (editingItem.variants || []) : [] })}
                      className="w-5 h-5 rounded border-[#141414]/20 text-[#141414] focus:ring-[#141414]"
                    />
                    <div className="flex items-center gap-2">
                      <GitBranch className="w-4 h-4 opacity-40" />
                      <span className="text-sm font-bold">Enable Variants</span>
                    </div>
                  </label>

                  {editingItem.hasVariants && (
                    <div className="space-y-6 p-4 bg-[#141414]/5 rounded-2xl border border-[#141414]/10 animate-in fade-in slide-in-from-top-2">
                      <div className="space-y-4">
                        <div className="flex items-center justify-between">
                          <label className="text-[10px] font-bold uppercase tracking-widest opacity-40">Options</label>
                          <button 
                            type="button"
                            onClick={() => setEditingItem({
                              ...editingItem,
                              variantOptions: [...(editingItem.variantOptions || []), { name: '', values: [] }]
                            })}
                            className="text-[10px] font-bold underline hover:opacity-70"
                          >
                            + Add Option
                          </button>
                        </div>

                        <div className="space-y-4">
                          {editingItem.variantOptions?.map((option, oIdx) => (
                            <div key={oIdx} className="space-y-2 p-3 bg-white rounded-xl border border-[#141414]/10">
                              <div className="flex gap-2">
                                <input 
                                  type="text"
                                  placeholder="Option Name (e.g. Color, Size)"
                                  value={option.name}
                                  onChange={(e) => setEditingItem({
                                    ...editingItem,
                                    variantOptions: editingItem.variantOptions?.map((o, i) => i === oIdx ? { ...o, name: e.target.value } : o)
                                  })}
                                  className="flex-1 bg-[#141414]/5 border border-[#141414]/10 p-2 rounded-lg text-xs outline-none font-bold"
                                />
                                <button 
                                  type="button"
                                  onClick={() => setEditingItem({
                                    ...editingItem,
                                    variantOptions: editingItem.variantOptions?.filter((_, i) => i !== oIdx)
                                  })}
                                  className="p-2 text-red-500 hover:bg-red-50 rounded-lg transition-colors"
                                >
                                  <XCircle className="w-4 h-4" />
                                </button>
                              </div>
                              <div className="space-y-2">
                                <div className="flex flex-wrap gap-2">
                                  {option.values.map((val, vIdx) => (
                                    <span key={vIdx} className="px-2 py-1 bg-[#141414] text-white text-[9px] font-bold rounded-full flex items-center gap-1">
                                      {val}
                                      <button 
                                        type="button"
                                        onClick={() => setEditingItem({
                                          ...editingItem,
                                          variantOptions: editingItem.variantOptions?.map((o, i) => i === oIdx ? { ...o, values: o.values.filter((_, j) => j !== vIdx) } : o)
                                        })}
                                        className="hover:text-red-400"
                                      >
                                        <XCircle className="w-3 h-3" />
                                      </button>
                                    </span>
                                  ))}
                                </div>
                                <input 
                                  type="text"
                                  placeholder="Add value and press Enter..."
                                  onKeyDown={(e) => {
                                    if (e.key === 'Enter') {
                                      e.preventDefault();
                                      const val = e.currentTarget.value.trim();
                                      if (val && !option.values.includes(val)) {
                                        setEditingItem({
                                          ...editingItem,
                                          variantOptions: editingItem.variantOptions?.map((o, i) => i === oIdx ? { ...o, values: [...o.values, val] } : o)
                                        });
                                        e.currentTarget.value = '';
                                      }
                                    }
                                  }}
                                  className="w-full bg-[#141414]/5 border border-[#141414]/10 p-2 rounded-lg text-[10px] outline-none font-bold"
                                />
                              </div>
                            </div>
                          ))}
                        </div>

                        <button 
                          type="button"
                          onClick={() => {
                            const generated = generateVariants(
                              editingItem.variantOptions || [], 
                              editingItem.unitPrice || 0, 
                              editingItem.costPrice || 0,
                              editingItem.code || 'VAR'
                            );
                            setEditingItem({ ...editingItem, variants: generated });
                          }}
                          className="w-full py-3 bg-[#141414] text-white text-[10px] font-bold rounded-xl hover:opacity-90 transition-all uppercase tracking-widest"
                        >
                          Generate Variants
                        </button>
                      </div>

                      {editingItem.variants && editingItem.variants.length > 0 && (
                        <div className="space-y-3">
                          <label className="text-[10px] font-bold uppercase tracking-widest opacity-40">Generated Variants</label>
                          <div className="space-y-2 max-h-80 overflow-y-auto custom-scrollbar pr-2">
                            <div className="grid grid-cols-7 gap-2 px-2 py-1 text-[8px] font-bold uppercase tracking-wider opacity-40">
                              <div className="flex items-center justify-center">Save</div>
                              <div className="col-span-2">Variant</div>
                              <div>Cost</div>
                              <div>Sell</div>
                              <div>Barcode</div>
                              <div className="text-center">Qty</div>
                            </div>
                            {(editingItem.variants || []).map((variant, vIdx) => (
                              <div key={variant.id} className={cn(
                                "grid grid-cols-7 gap-2 p-2 rounded-xl border items-center transition-all",
                                variant.save ? "bg-white border-[#141414]/10" : "bg-white/50 border-transparent opacity-50"
                              )}>
                                <div className="flex items-center justify-center">
                                  <input 
                                    type="checkbox"
                                    checked={!!variant.save}
                                    onChange={(e) => setEditingItem({
                                      ...editingItem,
                                      variants: editingItem.variants?.map((v, i) => i === vIdx ? { ...v, save: e.target.checked } : v)
                                    })}
                                    className="w-4 h-4 rounded border-[#141414]/20 text-[#141414] focus:ring-[#141414]"
                                  />
                                </div>
                                <div className="col-span-2 text-[10px] font-bold truncate">
                                  {variant.name}
                                  <span className="block text-[8px] opacity-40 font-mono">{variant.sku}</span>
                                </div>
                                <input 
                                  type="number"
                                  placeholder="Cost"
                                  value={isNaN(variant.costPrice) ? '' : variant.costPrice}
                                  onChange={(e) => setEditingItem({
                                    ...editingItem,
                                    variants: editingItem.variants?.map((v, i) => i === vIdx ? { ...v, costPrice: parseFloat(e.target.value) || 0 } : v)
                                  })}
                                  className="bg-[#141414]/5 border border-[#141414]/10 p-1 rounded text-[10px] outline-none text-center font-bold"
                                />
                                <input 
                                  type="number"
                                  placeholder="Sell"
                                  value={isNaN(variant.unitPrice) ? '' : variant.unitPrice}
                                  onChange={(e) => setEditingItem({
                                    ...editingItem,
                                    variants: editingItem.variants?.map((v, i) => i === vIdx ? { ...v, unitPrice: parseFloat(e.target.value) || 0 } : v)
                                  })}
                                  className="bg-[#141414]/5 border border-[#141414]/10 p-1 rounded text-[10px] outline-none text-center font-bold"
                                />
                                <input 
                                  type="text"
                                  placeholder="Barcode"
                                  value={variant.barcode}
                                  onChange={(e) => setEditingItem({
                                    ...editingItem,
                                    variants: editingItem.variants?.map((v, i) => i === vIdx ? { ...v, barcode: e.target.value } : v)
                                  })}
                                  className="bg-[#141414]/5 border border-[#141414]/10 p-1 rounded text-[10px] outline-none font-bold"
                                />
                                <input 
                                  type="number"
                                  placeholder="Qty"
                                  value={isNaN(variant.quantity) ? '' : variant.quantity}
                                  onChange={(e) => setEditingItem({
                                    ...editingItem,
                                    variants: editingItem.variants?.map((v, i) => i === vIdx ? { ...v, quantity: parseInt(e.target.value) || 0 } : v)
                                  })}
                                  className="bg-[#141414]/5 border border-[#141414]/10 p-1 rounded text-[10px] outline-none text-center font-bold"
                                />
                              </div>
                            ))}
                          </div>
                        </div>
                      )}
                    </div>
                  )}
                </div>
              </div>

              <div className="flex gap-3 pt-4 sticky bottom-0 bg-white pb-2">
                <button
                  type="button"
                  onClick={() => setIsEditModalOpen(false)}
                  className="flex-1 px-6 py-4 rounded-2xl font-bold hover:bg-[#141414]/5 transition-all"
                >
                  Cancel
                </button>
                <button
                  type="submit"
                  className="flex-[2] px-6 py-4 bg-[#141414] text-white rounded-2xl font-bold hover:opacity-90 transition-all flex items-center justify-center gap-2"
                >
                  <Check className="w-5 h-5" />
                  Save Changes
                </button>
              </div>
            </form>
          </div>
        </div>
      )}
      {/* Catalog QR Modal */}
      {isCatalogQrModalOpen && (() => {
        const urlWithoutHash = window.location.href.split('#')[0];
        const cleanBaseUrl = urlWithoutHash.endsWith('/') ? urlWithoutHash.slice(0, -1) : urlWithoutHash;
        const catalogUrl = `${cleanBaseUrl}/#/catalog/${orgId || localStorage.getItem('selected_org_id') || ''}`;
        
        return (
          <div className="fixed inset-0 bg-[#141414]/60 backdrop-blur-md z-[200] flex items-center justify-center p-4 animate-in fade-in duration-200">
            <div 
              className={cn(
                "relative w-full max-w-md bg-white shadow-2xl overflow-hidden p-10 text-center animate-in zoom-in-95 duration-200",
                isCherry ? "rounded-[3.5rem] border-4 border-[#FFCCD5]" : "rounded-[3rem]"
              )}
            >
              <div className="space-y-6">
                <div>
                  <h2 className={cn(
                    "text-2xl font-extrabold tracking-tight uppercase",
                    isCherry && "font-serif capitalize italic text-[#590D22]"
                  )}>Catalog Landing Page</h2>
                  <p className={cn(
                    "text-xs font-bold uppercase tracking-widest mt-1",
                    isCherry ? "text-[#FF85A1]" : "text-muted-foreground"
                  )}>Scan for Website Access</p>
                </div>

                <div className={cn(
                  "p-8 rounded-[2.5rem] flex items-center justify-center transition-colors",
                  isCherry ? "bg-[#FFF0F3]" : "bg-[#E4E3E0]"
                )}>
                  <QRCodeSVG 
                    value={catalogUrl}
                    size={256}
                    level="H"
                    includeMargin
                    imageSettings={{
                      src: "/favicon.ico",
                      x: undefined,
                      y: undefined,
                      height: 48,
                      width: 48,
                      excavate: true,
                    }}
                  />
                </div>

                <div className="space-y-3">
                  <p className={cn(
                    "text-sm font-medium italic serif",
                    isCherry ? "text-[#800F2F]" : "text-slate-500"
                  )}>
                    This QR code links directly to your public product catalog website where customers can view all details.
                  </p>
                  <div className="flex gap-3">
                    <button 
                      onClick={() => window.open(catalogUrl, '_blank')}
                      className={cn(
                        "flex-1 flex items-center justify-center gap-2 py-4 rounded-2xl font-bold text-xs uppercase tracking-widest transition-colors",
                        isCherry ? "bg-[#FFCCD5] text-[#590D22] hover:bg-[#FFB3C1]" : "bg-slate-100 hover:bg-slate-200"
                      )}
                    >
                      <ExternalLink className="w-4 h-4" />
                      Preview 
                    </button>
                    <button 
                      onClick={() => setIsCatalogQrModalOpen(false)}
                      className={cn(
                        "flex-1 py-4 rounded-2xl font-bold text-xs uppercase tracking-widest shadow-lg transition-all",
                        isCherry ? "bg-[#590D22] text-white shadow-[#590D22]/20" : "bg-primary text-white shadow-primary/20"
                      )}
                    >
                      Done
                    </button>
                  </div>
                </div>
              </div>
            </div>
          </div>
        );
      })()}
    </div>
  );
}
